// ============================================================================
// Blanc Space custom global behaviors
// Loaded on every page (see layout/theme.liquid). Keep site-wide tweaks here;
// product-picker behaviors live in variant-picker-cases.js.
// ============================================================================

(() => {
	if (window.__blancCustomInitialized) return;
	window.__blancCustomInitialized = true;

	/* --- Slide-up sheets on mobile --------------------------------------------
	   The theme's cart drawer and added-to-cart popup use a clip-path wipe;
	   give them the same slide-up motion as the nav menu and the picker's
	   sheets. Desktop keeps each element's vanilla behavior. Implemented as
	   runtime prototype patches so no vanilla section markup changes. */

	const EASE = 'cubic-bezier(0.4, 0, 0.22, 1)';
	const isMobile = () => window.matchMedia('screen and (max-width: 999px)').matches;
	const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	/* An explicit accessory add inside the already-open cart belongs to that
	   drawer. Mark the bubbling add event before Impact's document listeners run
	   so the drawer can refresh in place without stacking the mini cart above it. */
	document.addEventListener('variant:add', (event) => {
		if (event.detail && event.target.closest?.('cart-drawer')) {
			event.detail.blockCartDrawerOpening = true;
		}
	}, true);

	/* --- Collection filtering/promotion helpers -------------------------------
	   These functions are the browser half of the hybrid lifecycle documented in
	   AGENTS.md. Aesthetic tags use OR semantics; promotions are positioned by
	   visible product count after filtering, never by raw DOM/Shopify position. */
	const collectionAestheticTags = () => new URL(window.location.href).searchParams
		.getAll('filter.p.tag')
		.flatMap((value) => value.split(','))
		.map((value) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
		.filter(Boolean);
	const productMatchesCollectionAesthetics = (product, activeTags = collectionAestheticTags()) => {
		if (!activeTags.length || !product?.hasAttribute('data-product-index')) return true;
		const productTags = product.dataset.aestheticTags || '';
		return activeTags.some((tag) => productTags.includes(`|${tag}|`));
	};
	const reconcileCollectionPromos = (scope = document) => {
		const grids = [];
		if (scope instanceof Element && scope.matches('product-list')) grids.push(scope);
		grids.push(...(scope.querySelectorAll?.('product-list') || []));

		grids.forEach((grid) => {
			const products = Array.from(grid.querySelectorAll(':scope > [data-product-index]')).filter((product) => !product.hidden);
			const promos = Array.from(grid.querySelectorAll(':scope > [data-promo-position]'))
				.sort((a, b) => Number(a.dataset.promoPosition) - Number(b.dataset.promoPosition));

			promos.forEach((promo) => {
				const position = Number.parseInt(promo.dataset.promoPosition || '', 10);
				const anchor = Number.isFinite(position) ? products[position - 1] : null;
				promo.hidden = !anchor;
				if (anchor) grid.insertBefore(promo, anchor);
			});
		});
	};
	const applyCollectionAestheticFilter = (scope = document) => {
		const activeTags = collectionAestheticTags();
		scope.querySelectorAll?.('product-list [data-product-index]').forEach((product) => {
			product.hidden = !productMatchesCollectionAesthetics(product, activeTags);
		});
		reconcileCollectionPromos(scope);
	};
	window.BlancSpaceApplyCollectionAesthetics = applyCollectionAestheticFilter;
	const hydrateSingleAestheticCollection = () => {
		const activeTags = collectionAestheticTags();
		if (activeTags.length !== 1) return;

		const section = document.querySelector('.shopify-section--main-collection');
		if (!section?.id) return;

		const constraint = activeTags[0];
		const currentUrl = new URL(window.location.href);
		if (currentUrl.searchParams.get('constraint') === constraint) return;

		// Product tags are intentionally absent from Search & Discovery's public
		// facets. Shopify's collection constraint route still filters them on the
		// server, however, so a single aesthetic can arrive as one correctly
		// paginated response instead of scanning sparse catalogue pages in the
		// browser. facet:update keeps this implementation detail out of the URL.
		currentUrl.searchParams.set('constraint', constraint);
		currentUrl.searchParams.set('section_id', section.id.replace('shopify-section-', ''));
		window.__blancCollectionAestheticHydrating = true;
		document.dispatchEvent(new CustomEvent('facet:update', {
			bubbles: true,
			detail: { url: currentUrl }
		}));
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			applyCollectionAestheticFilter();
			hydrateSingleAestheticCollection();
		});
	} else {
		applyCollectionAestheticFilter();
		hydrateSingleAestheticCollection();
	}

	/* The filter sheet stages changes until Apply. Keep a live, removable
	   summary in its footer and update the heading count without exposing
	   active-filter chips above the collection grid. */
	class CollectionFilterSelection extends HTMLElement {
		connectedCallback() {
			this.form = document.getElementById(this.getAttribute('form'));
			if (!this.form) return;

			this.chips = this.querySelector('[data-bs-filter-selection-chips]');
			this.clearButton = this.querySelector('[data-bs-clear-staged-filters]');
			this._render = this._render.bind(this);
			this.form.addEventListener('change', this._render);
			this.form.addEventListener('facet:selection-change', this._render);
			this.form.addEventListener('reset', () => requestAnimationFrame(this._render));
			this.addEventListener('click', (event) => this._onClick(event));
			this._render();
		}

		disconnectedCallback() {
			this.form?.removeEventListener('change', this._render);
			this.form?.removeEventListener('facet:selection-change', this._render);
		}

		get selectedInputs() {
			return Array.from(this.form?.querySelectorAll('input:checked[data-bs-filter-label]') || []);
		}

		_onClick(event) {
			const removeButton = event.target.closest('[data-bs-remove-staged-filter]');
			if (removeButton) {
				const input = this.form.querySelector(`#${CSS.escape(removeButton.dataset.inputId)}`);
				if (input) {
					input.checked = false;
					input.dispatchEvent(new Event('change', { bubbles: true }));
				}
				return;
			}

			if (!event.target.closest('[data-bs-clear-staged-filters]')) return;
			this.selectedInputs.forEach((input) => { input.checked = false; });
			this.form.dispatchEvent(new Event('change', { bubbles: true }));
		}

		_render() {
			if (!this.form || !this.chips) return;
			const selected = this.selectedInputs;
			this.chips.replaceChildren(...selected.map((input) => {
				const chip = document.createElement('button');
				chip.type = 'button';
				chip.className = 'bs-collection-filter-selection__chip';
				chip.dataset.bsRemoveStagedFilter = '';
				chip.dataset.inputId = input.id;
				chip.setAttribute('aria-label', `Remove ${input.dataset.bsFilterLabel} filter`);

				if (input.dataset.bsFilterSwatch) {
					const swatch = document.createElement('span');
					swatch.className = `bs-collection-filter-selection__swatch bs-collection-filter-selection__swatch--${input.dataset.bsFilterSwatch}`;
					swatch.setAttribute('aria-hidden', 'true');
					chip.appendChild(swatch);
				}

				const label = document.createElement('span');
				label.textContent = input.dataset.bsFilterLabel;
				chip.append(label);

				const close = document.createElement('span');
				close.className = 'bs-collection-filter-selection__remove';
				close.textContent = '×';
				close.setAttribute('aria-hidden', 'true');
				chip.append(close);
				return chip;
			}));

			this.hidden = selected.length === 0;
			const titleCount = this.closest('facet-drawer')?.querySelector('[data-bs-filter-title-count]');
			if (titleCount) titleCount.textContent = selected.length ? ` (${selected.length})` : '';
			const section = document.getElementById(`shopify-section-${this.form.getAttribute('section-id')}`);
			const toolbarLabel = section?.querySelector('[data-bs-filter-toolbar-label]');
			if (toolbarLabel) toolbarLabel.textContent = `Filter & sort${selected.length ? ` (${selected.length})` : ''}`;
		}
	}

	if (!customElements.get('collection-filter-selection')) {
		customElements.define('collection-filter-selection', CollectionFilterSelection);
	}

	const patchSlideUp = (tagName) => {
		customElements.whenDefined(tagName).then(() => {
			const El = customElements.get(tagName);
			const originalShow = El.prototype._showTransition;
			const originalHide = El.prototype._hideTransition;

			El.prototype._showTransition = function (animate = true) {
				const content = this.shadowRoot && this.shadowRoot.querySelector('[part="content"]');
				if (!isMobile() || !content) return originalShow.call(this, animate);
				// The vanilla drawer sets its physical bottom/top anchoring inside its
				// own transition. Our replacement animation must preserve that step or
				// the notification appears at its source position higher up the page.
				if (typeof this._setInitialPosition === 'function') this._setInitialPosition();
				this.style.display = 'block';
				this.style.visibility = 'visible';
				this.style.opacity = '1';
				content.getAnimations().forEach((a) => a.cancel());
				const anim = content.animate(
					[{ transform: 'translateY(110%)' }, { transform: 'translateY(0)' }],
					{ duration: 400, easing: EASE, fill: 'both' }
				);
				if (!animate || reduced()) anim.finish();
				// A cancelled animation REJECTS .finished; an unsettled promise here
				// swallows dialog:after-show and leaves the drawer half-open.
				return anim.finished.catch(() => {});
			};

			El.prototype._hideTransition = function () {
				const content = this.shadowRoot && this.shadowRoot.querySelector('[part="content"]');
				if (!isMobile() || !content) return originalHide.call(this);
				content.getAnimations().forEach((a) => a.cancel());
				const anim = content.animate(
					[{ transform: 'translateY(0)' }, { transform: 'translateY(110%)' }],
					{ duration: 300, easing: EASE, fill: 'both' }
				);
				if (reduced()) anim.finish();
				// .catch first: a cancelled hide must still reach the terminal
				// hidden state and let dialog:after-hide fire.
				return anim.finished.catch(() => {}).then(() => {
					this.style.opacity = '0';
					this.style.visibility = 'hidden';
					this.style.display = 'none';
				});
			};
		});
	};

	patchSlideUp('cart-notification-drawer');

	/* --- Cart in the pill --------------------------------------------------------
	   The cart is a copy of the nav-menu mechanic: its whole section wrapper
	   moves inside the header pill, and on mobile (<1150px, the menu's own
	   breakpoint) opening the cart EXPANDS THE PILL downward exactly like
	   the menu (max-height transition, store-header[expanded], icons swap to
	   "Your Cart" + close via :has() CSS). On desktop it stays a compact
	   Nomad-style panel that slides down from under the header. */

	const initCartInPill = () => {
		const wrapper = document.querySelector('.header-pill .header__wrapper');
		const cart = document.getElementById('cart-drawer');
		// The wrapper id carries the section-group hash, so find it from the
		// drawer itself
		const cartSection = cart && cart.closest('.shopify-section');
		if (wrapper && cartSection) wrapper.appendChild(cartSection);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initCartInPill);
	} else {
		initCartInPill();
	}

	// Opening the menu closes the cart (the reverse is handled in the patch)
	document.addEventListener('click', (event) => {
		if (!event.target.closest || !event.target.closest('button[aria-controls="header-menu"]')) return;
		const cart = document.getElementById('cart-drawer');
		if (cart && cart.hasAttribute('open') && typeof cart.hide === 'function') cart.hide();
	});

	// Tapping the header's page dimmer closes whichever pill drawer owns it.
	// The menu and search carry their own overlay handlers; the cart/wishlist
	// had none, so a dimmer tap used to strip the pill state without actually
	// closing them — stranding the blur over an open drawer.
	document.addEventListener('click', (event) => {
		if (event.target.getAttribute?.('part') !== 'overlay' || !event.target.closest('store-header')) return;
		const cart = document.getElementById('cart-drawer');
		if (cart?.hasAttribute('open') && typeof cart.hide === 'function') cart.hide();
		const wishlist = document.getElementById('wishlist-drawer');
		if (wishlist?.hasAttribute('open') && typeof wishlist.hide === 'function') wishlist.hide();
	});

	const isPillCart = () => window.matchMedia('screen and (max-width: 1149px)').matches;

	// Safari's bottom bar collapses/expands the visible viewport mid-scroll —
	// re-measure so the open cart keeps filling it.
	window.addEventListener('resize', () => {
		const cart = document.getElementById('cart-drawer');
		if (cart && cart.classList.contains('bs-cart-open') && typeof cart._sizeToViewport === 'function') {
			cart._sizeToViewport();
		}
	});

	/* Shared open/close animation for the desktop Nomad panels (cart + search):
	   the panel drops down from under the pill while the page dim fades, so
	   both overlays move identically instead of the vendor's side wipe. */
	const panelDropAnimation = (drawer, show, animate = true) => {
		const content = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="content"]');
		if (!content) return Promise.resolve();
		const overlay = drawer.shadowRoot.querySelector('[part="overlay"]');
		content.getAnimations().forEach((a) => a.cancel());
		if (overlay) {
			overlay.getAnimations().forEach((a) => a.cancel());
			const fade = overlay.animate(
				show ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
				{ duration: 250, easing: 'ease', fill: 'both' }
			);
			if (!animate || reduced()) fade.finish();
		}
		const frames = [
			{ transform: 'translateY(-6%)', opacity: 0 },
			{ transform: 'translateY(0)', opacity: 1 },
		];
		if (!show) frames.reverse();
		const anim = content.animate(frames, { duration: show ? 350 : 250, easing: EASE, fill: 'both' });
		if (!animate || reduced()) anim.finish();
		// A rapid re-open cancels the in-flight animation and REJECTS .finished,
		// which would swallow dialog:after-show/after-hide upstream — settle it.
		return anim.finished.catch(() => {});
	};

	customElements.whenDefined('cart-drawer').then(() => {
		const El = customElements.get('cart-drawer');

		El.prototype._showTransition = function (animate = true) {
			const storeHeader = this.closest('store-header');
			const search = storeHeader?.querySelector('search-mobile[open]');
			if (search && typeof search.hide === 'function') search.hide();
			this.style.display = 'block';
			this.style.visibility = 'visible';
			this.style.opacity = '1';

			if (isPillCart() && storeHeader) {
				// Pill expansion — same mechanic as navigation-box
				const nav = storeHeader.querySelector('navigation-box');
				if (nav && nav.open) nav.onSummaryClicked();
				// Ownership flag, set synchronously: the bs-cart-open class only
				// lands two frames later, so a hide inside that window must still
				// find proof that this drawer expanded the pill and tear it down —
				// otherwise store-header[expanded] and the blur dimmer are stranded.
				this._bsPillOpen = true;
				storeHeader.setAttribute('expanded', '');
				// Fill down to just above the browser chrome: measure where the
				// (still collapsed) panel starts and claim the rest of the
				// visible viewport, less the pill's own bottom inset.
				this._sizeToViewport();
				// The menu's page-dimmer, not the drawer's own overlay (which
				// would dim the pill itself — CSS hides it in pill mode)
				const pageOverlay = storeHeader.querySelector('div[part="overlay"]');
				if (pageOverlay) pageOverlay.removeAttribute('hidden');
				// two frames so the max-height transition starts from 0
				return new Promise((resolve) => {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							if (this._bsPillOpen) this.classList.add('bs-cart-open');
							setTimeout(resolve, reduced() || !animate ? 0 : 420);
						});
					});
				});
			}

			return panelDropAnimation(this, true, animate);
		};

		El.prototype._sizeToViewport = function () {
			const PILL_INSET = 0; // full-bleed: the panel runs to the viewport bottom; the footer carries safe-area padding instead
			// The nav and cart share the same grid row. If the nav is open or
			// animating closed, the cart's own top is temporarily pushed to the
			// bottom of the menu and produces a tiny (240px) stale measurement.
			// The nav row's top is stable throughout the handoff and is exactly
			// where the cart lands once the menu has collapsed.
			const nav = this.closest('store-header')?.querySelector('navigation-box');
			const top = nav?.getBoundingClientRect().top ?? this.getBoundingClientRect().top;
			const available = Math.max(240, Math.round(window.innerHeight - top - PILL_INSET));
			this.style.setProperty('--bs-cart-h', available + 'px');
		};

		El.prototype._hideTransition = function () {
			const storeHeader = this.closest('store-header');

			if ((this._bsPillOpen || this.classList.contains('bs-cart-open')) && storeHeader) {
				this._bsPillOpen = false;
				this.classList.remove('bs-cart-open');
				// The pill's swapped top row (title + X) must stay in place for the
				// whole 420ms collapse: `open` is gone instantly, and the icon row that
				// replaced it put the hamburger 11px from where the X was — a second
				// tap on the X opened the menu (2026-09-07 drawer investigation).
				// custom.css keys the swap on `#cart-drawer.bs-cart-closing` too.
				this.classList.add('bs-cart-closing');
				const nav = storeHeader.querySelector('navigation-box');
				const wishlist = document.getElementById('wishlist-drawer');
				const searchOpen = storeHeader.querySelector('search-mobile[open]');
				const siblingOwnsPill = (nav && nav.open) || (wishlist && wishlist.hasAttribute('open')) || searchOpen;
				if (!siblingOwnsPill) {
					storeHeader.removeAttribute('expanded');
					const pageOverlay = storeHeader.querySelector('div[part="overlay"]');
					if (pageOverlay) pageOverlay.setAttribute('hidden', '');
				}
				return new Promise((resolve) => {
					setTimeout(() => {
						this.classList.remove('bs-cart-closing');
						this.style.opacity = '0';
						this.style.visibility = 'hidden';
						this.style.display = 'none';
						resolve();
					}, reduced() ? 0 : 420);
				});
			}

			// Desktop panel: the vendor makes the drawer inert the instant hide()
			// runs, so a click during the 300ms drop fell through to the page
			// beneath (one test navigated away). Stay interactive until the drop
			// settles; the panel's own overlay then swallows the click harmlessly.
			this.removeAttribute('inert');
			return panelDropAnimation(this, false).then(() => {
				this.setAttribute('inert', '');
				this.style.opacity = '0';
				this.style.visibility = 'hidden';
				this.style.display = 'none';
			});
		};
	});

	/* --- BLANC SPACE CUSTOM: desktop search panel matches the cart ----------
	   From 1150px the search drawer is the same Nomad panel under the pill, so
	   it opens/closes with the shared drop animation instead of the vendor's
	   side-drawer wipe. Tablet (700–1149px) keeps the vendor transition. */
	customElements.whenDefined('search-drawer').then(() => {
		const El = customElements.get('search-drawer');
		const vendorShow = El.prototype._showTransition;
		const vendorHide = El.prototype._hideTransition;
		const clearPanelAnimations = (drawer) => {
			['content', 'overlay'].forEach((part) => {
				drawer.shadowRoot?.querySelector(`[part="${part}"]`)?.getAnimations().forEach((a) => a.cancel());
			});
		};

		El.prototype._showTransition = function (animate = true) {
			if (isPillCart()) {
				// A persisted desktop fill-both state (or the desktop close's
				// inline display:none) would keep the vendor's side drawer
				// invisible after a resize.
				clearPanelAnimations(this);
				this.style.display = '';
				this.style.visibility = '';
				this.style.opacity = '';
				return vendorShow.call(this, animate);
			}
			this.style.display = 'block';
			this.style.visibility = 'visible';
			this.style.opacity = '1';
			return panelDropAnimation(this, true, animate);
		};

		El.prototype._hideTransition = function () {
			if (isPillCart()) {
				clearPanelAnimations(this);
				return vendorHide.call(this);
			}
			// See the cart drawer note: stay interactive while dropping closed so a
			// second click lands on this panel's overlay, not the page beneath.
			this.removeAttribute('inert');
			return panelDropAnimation(this, false).then(() => {
				this.setAttribute('inert', '');
				this.style.opacity = '0';
				this.style.visibility = 'hidden';
				this.style.display = 'none';
			});
		};
	});

	/* Serialize the drawer's delayed content swap. Vendor _onCartChanged
	   schedules `currentInner.innerHTML = updatedInner.innerHTML` 1250ms out
	   (so the remove/line animations can play), but never cancels it: a
	   quantity change followed within ~1s by an in-drawer upsell add let the
	   older render overwrite the newer one, visually removing the just-added
	   item until the next cart event. Each new cart:change now supersedes any
	   pending swap, and focus is restored to the same control (by id) so the
	   quantity select survives the rebuild for keyboard/VoiceOver users. The
	   empty-cart and degenerate paths delegate to the vendor implementation. */
	customElements.whenDefined('cart-drawer').then(() => {
		const El = customElements.get('cart-drawer');
		const vendorOnCartChanged = El.prototype._onCartChanged;

		El.prototype._onCartChanged = function (event) {
			const cartDetail = event.detail?.cart;
			if (!cartDetail || cartDetail.item_count === 0) {
				if (this._bsPendingSwap) {
					clearTimeout(this._bsPendingSwap);
					this._bsPendingSwap = null;
				}
				return vendorOnCartChanged.call(this, event);
			}

			const sectionId = (this.closest('.shopify-section')?.id || '').replace(/^shopify-section-/, '');
			const sectionHtml = cartDetail.sections?.[sectionId];
			const currentInner = this.querySelector('.cart-drawer__inner');
			const footer = this.querySelector('[slot="footer"]');
			if (!sectionHtml || !currentInner || !footer) return vendorOnCartChanged.call(this, event);

			const updatedContent = new DOMParser().parseFromString(sectionHtml, 'text/html');
			const updatedInner = updatedContent.querySelector('.cart-drawer__inner');
			const updatedFooter = updatedContent.querySelector('[slot="footer"]');
			if (!updatedInner || !updatedFooter) return vendorOnCartChanged.call(this, event);

			if (this._bsPendingSwap) clearTimeout(this._bsPendingSwap);
			const swap = () => {
				this._bsPendingSwap = null;
				const active = document.activeElement;
				const focusId = active && active.id && currentInner.contains(active) ? active.id : null;
				currentInner.innerHTML = updatedInner.innerHTML;
				if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
			};

			// Keep the vendor's timing: adds swap on the next macrotask, other
			// changes after the 1250ms line animation window.
			const delay = event.detail.baseEvent === 'variant:add' ? 0 : 1250;
			this._bsPendingSwap = setTimeout(swap, delay);
			footer.replaceChildren(...updatedFooter.childNodes);
		};

		// The vendor constructor binds _onCartChanged before this patch runs,
		// so the already-constructed drawer still points at the old method.
		// Rebind the live instance's listener to the patched prototype method;
		// the stored reference keeps disconnect/reconnect (the cart-in-pill
		// move) removing and re-adding the correct listener.
		const drawer = document.getElementById('cart-drawer');
		if (drawer && drawer._onCartChangedListener) {
			document.removeEventListener('cart:change', drawer._onCartChangedListener);
			drawer._onCartChangedListener = El.prototype._onCartChanged.bind(drawer);
			document.addEventListener('cart:change', drawer._onCartChangedListener);
		}
	});

	/* Safety net for `is="close-button"` buttons that never upgraded. Vendor
	   drawer re-renders adopt DOMParser-created nodes, and some desktop Chrome
	   sessions end up with an inert close button (PostHog: 60+ sessions rage-
	   clicking the emptied cart drawer's X, 2026-08-20/21). An upgraded button
	   handles the click itself; an un-upgraded one falls through to this. */
	document.addEventListener('click', (event) => {
		const button = event.target.closest?.('button[is="close-button"]');
		if (!button) return;
		const CloseButton = customElements.get('close-button');
		if (CloseButton && button instanceof CloseButton) return; // healthy: vendor handles it
		const openable = button.closest('[open]');
		if (openable && typeof openable.hide === 'function') openable.hide();
	});

	/* Upsell removal tombstone (user testing: the auto-preselected screen
	   protector re-adding itself after a shopper deleted it reads as sneaky).
	   Any cart-card removal records the product id for THIS session;
	   upsell-protector.js consults the same key before preselecting, and an
	   explicit re-add (PDP checkbox or cart recommendation) clears it. */
	const UPSELL_REMOVED_KEY = 'blancspace.upsellRemoved';
	const readRemovedUpsells = () => {
		try { return new Set((JSON.parse(sessionStorage.getItem(UPSELL_REMOVED_KEY) || '[]') || []).map(String)); } catch (error) { return new Set(); }
	};
	const writeRemovedUpsells = (set) => {
		try { sessionStorage.setItem(UPSELL_REMOVED_KEY, JSON.stringify([...set])); } catch (error) { /* storage unavailable */ }
	};

	// Capture phase: the vendor's line-item handler may stop propagation.
	document.addEventListener('click', (event) => {
		const remove = event.target.closest?.('.line-item--cases__remove[data-product-id]');
		if (!remove) return;
		const set = readRemovedUpsells();
		set.add(String(remove.getAttribute('data-product-id')));
		writeRemovedUpsells(set);
	}, true);

	document.addEventListener('variant:add', (event) => {
		const items = event.detail?.items;
		if (!Array.isArray(items) || !items.length) return;
		const set = readRemovedUpsells();
		let changed = false;
		items.forEach((item) => {
			if (item?.product_id != null && set.delete(String(item.product_id))) changed = true;
		});
		if (changed) writeRemovedUpsells(set);
	});

	/* Quantity errors in the compact cart card. Vendor LineItemQuantity's
	   failure path expects Impact's .line-item__info / .quantity-input markup;
	   the drawer's line-item-cases card has neither, so a rejected
	   /cart/change.js (e.g. quantity above stock) threw before any message
	   rendered, leaving the pill-loader busy and the select showing a quantity
	   the cart doesn't hold. Inside the custom card we run the request
	   ourselves with a card-appropriate error state and the same event
	   contract; everywhere else the vendor path is untouched. */
	customElements.whenDefined('line-item-quantity').then(() => {
		const El = customElements.get('line-item-quantity');
		const vendorChange = El.prototype._changeLineItemQuantity;

		El.prototype._changeLineItemQuantity = async function (lineKey, targetQuantity) {
			const card = this.closest('.line-item--cases');
			if (!card) return vendorChange.call(this, lineKey, targetQuantity);

			const lineItem = this.closest('line-item');
			const select = this.querySelector('select[data-line-key]');
			const resetSelect = () => {
				if (!select) return;
				for (const option of select.options) option.selected = option.defaultSelected;
			};
			const showError = (description) => {
				card.querySelector('[role="alert"]')?.remove();
				const message = document.createElement('p');
				message.className = 'text-xs text-error line-item--cases__error';
				message.setAttribute('role', 'alert');
				message.textContent = description || 'Something went wrong — please try again.';
				(card.querySelector('.line-item--cases__body') || card).appendChild(message);
				resetSelect();
				const failedBundleRow = this.closest('.line-item--cases__bundle');
				if (failedBundleRow) {
					failedBundleRow.style.opacity = '';
					failedBundleRow.style.pointerEvents = '';
				} else {
					lineItem?.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
				}
			};

			// A nested bundle-row removal (screen protector under its case) must
			// NOT route through the host case's <line-item> events: the vendor's
			// LineItem._onChanged sees quantity 0 and animates the whole CASE
			// card away before the re-render restores it. The row fades itself
			// instead and only the cart:change re-render applies the result.
			const bundleRow = this.closest('.line-item--cases__bundle');
			if (bundleRow) {
				bundleRow.style.opacity = '0.45';
				bundleRow.style.pointerEvents = 'none';
			} else {
				lineItem?.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity } }));
			}
			const sectionsToBundle = [];
			document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

			// Removing the LAST real item leaves only the hidden Shipping
			// Protection line, which the reconciler then removes with a second
			// request (and, on /cart, a second reload). Bundle both removals into
			// one cart/update.js call so the cart goes straight to genuinely empty.
			const surface = this.closest('cart-drawer, .cart') || document;
			const isLastRealItem = targetQuantity === 0 && surface.querySelectorAll('.line-item--cases').length === 1;
			const protectionVariantId = window.__bsProtectionVariantId;
			const bundleProtectionRemoval = isLastRealItem && protectionVariantId && (window.__bsProtectionQty || 0) > 0;

			let responseContent;
			try {
				const request = bundleProtectionRemoval
					? { url: `${Shopify.routes.root}cart/update.js`, body: { updates: { [lineKey]: 0, [protectionVariantId]: 0 }, sections: sectionsToBundle } }
					: { url: `${Shopify.routes.root}cart/change.js`, body: { id: lineKey, quantity: targetQuantity, sections: sectionsToBundle } };
				const response = await fetch(request.url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(request.body)
				});
				responseContent = await response.json();
				if (!response.ok) return showError(responseContent?.description);
			} catch (error) {
				return showError();
			}

			card.querySelector('[role="alert"]')?.remove();
			if (window.themeVariables?.settings?.pageType === 'cart') return window.location.reload();
			const changedItem = responseContent.items?.find((item) => item.key === lineKey);
			if (!bundleRow) {
				lineItem?.dispatchEvent(new CustomEvent('line-item:change', {
					bubbles: true,
					detail: { quantity: changedItem ? changedItem.quantity : 0, cart: responseContent }
				}));
			}
			document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
				bubbles: true,
				detail: { baseEvent: 'line-item:change', cart: responseContent }
			}));
		};
	});

	/* While a pill drawer is collapsing (420ms, `.bs-cart-closing` /
	   `.bs-wishlist-closing`), its swapped header row stays visible so the X
	   cannot be replaced under the finger — but a second tap on that X must be
	   a no-op, not a toggle: the vendor `aria-controls` button re-opened the
	   drawer while the pending hide timeout then set display:none, leaving an
	   "open" but invisible cart with the header icons hidden. Swallow every
	   click inside the drawer's pill head during the closing window. */
	document.addEventListener('click', (event) => {
		const target = event.target instanceof Element ? event.target : null;
		if (!target) return;
		const closingCart = document.getElementById('cart-drawer')?.classList.contains('bs-cart-closing');
		const closingWishlist = document.getElementById('wishlist-drawer')?.classList.contains('bs-wishlist-closing');
		if (!closingCart && !closingWishlist) return;
		if (target.closest('.bs-cart-head, .bs-wishlist-head, [aria-controls="cart-drawer"], [aria-controls="wishlist-drawer"]')) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}
	}, true);

	/* Filter drawer: the vendor makes it inert the instant `open` is removed while
	   it stays visible for ~300ms, and its X sits exactly over the header
	   hamburger — a second tap opened the menu (2026-09-07 investigation). Keep
	   the drawer interactive for the collapse (its own overlay swallows the tap)
	   and ignore hamburger taps for 450ms after a facet drawer hides. */
	customElements.whenDefined('facet-drawer').then(() => {
		const El = customElements.get('facet-drawer');
		const vendorHide = El.prototype._hideTransition;
		if (typeof vendorHide !== 'function') return;
		El.prototype._hideTransition = function () {
			this.removeAttribute('inert');
			window.__bsFacetDrawerHiddenAt = performance.now();
			const result = vendorHide.call(this);
			const settle = () => { if (!this.hasAttribute('open')) this.setAttribute('inert', ''); };
			if (result && typeof result.then === 'function') return result.then(settle, settle);
			settle();
			return result;
		};
	}).catch(() => {});
	document.addEventListener('click', (event) => {
		const button = event.target instanceof Element ? event.target.closest('button[aria-controls="header-menu"]') : null;
		if (!button) return;
		if (performance.now() - (window.__bsFacetDrawerHiddenAt || 0) < 450) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}
	}, true);

	/* View-cart actions inside the added-to-cart sheet should hand off to the
	   custom cart drawer, not navigate to Shopify's full cart page. Waiting for
	   the first dialog to finish closing also prevents two focus traps/drawers
	   competing during the transition. The href remains as a no-JS fallback. */
	document.addEventListener('click', (event) => {
		const trigger = event.target.closest?.('[data-open-cart-drawer]');
		if (!trigger) return;

		event.preventDefault();
		event.stopPropagation();
		const cart = document.getElementById('cart-drawer');
		if (!cart || typeof cart.show !== 'function') {
			window.location.assign(trigger.href || (window.Shopify?.routes?.root || '/') + 'cart');
			return;
		}

		const source = trigger.closest('cart-notification-drawer, quick-buy-drawer');
		const openCart = () => {
			if (!cart.open) cart.show();
		};

		if (source?.open && typeof source.hide === 'function') {
			/* dialog:after-hide never fires if the source's close animation gets
			   cancelled mid-flight (rejected .finished inside the vendor drawer),
			   which used to leave its full-page overlay painted over the cart.
			   The timeout guarantees the handoff either way. */
			let handed = false;
			const handoff = () => {
				if (handed) return;
				handed = true;
				if (!source.hasAttribute('open')) source.style.display = 'none';
				openCart();
			};
			source.addEventListener('dialog:after-hide', handoff, { once: true });
			setTimeout(handoff, 600);
			source.hide();
		} else {
			openCart();
		}
	}, true);

	/* --- Infinite reveal (Pinterest-style batching) -----------------------------
	   Sentinel element placed after a container whose children carry
	   hidden + data-batch="n" attributes. As the sentinel approaches the
	   viewport, the next batch is unhidden; when nothing is left the
	   sentinel retires. Site-wide home because collection-page infinite
	   scroll will reuse this element (fetch-and-append mode to come).

	   Attributes:
	   * target: optional CSS selector for the batched container
	              (defaults to the previous sibling element). */

	class InfiniteReveal extends HTMLElement {
		connectedCallback() {
			const targetSel = this.getAttribute('target');
			this._container = targetSel ? document.querySelector(targetSel) : this.previousElementSibling;
			if (!this._container || !this._container.querySelector('[data-batch][hidden]')) return;
			this._io = new IntersectionObserver(
				(entries) => {
					if (entries.some((e) => e.isIntersecting)) this._revealNext();
				},
				{ rootMargin: '600px 0px' }
			);
			this._io.observe(this);
		}

		disconnectedCallback() {
			if (this._io) this._io.disconnect();
		}

		_revealNext() {
			const hidden = Array.from(this._container.querySelectorAll('[data-batch][hidden]'));
			if (!hidden.length) {
				this._io.disconnect();
				return;
			}
			const next = Math.min(...hidden.map((el) => parseInt(el.getAttribute('data-batch'), 10)));
			hidden.forEach((el) => {
				if (parseInt(el.getAttribute('data-batch'), 10) === next) el.hidden = false;
			});
			if (!this._container.querySelector('[data-batch][hidden]')) {
				this._io.disconnect();
				return;
			}
			// The observer only reports transitions; if the sentinel is still
			// inside the trigger zone after the grid grew (short pages, fast
			// scrolls), re-observing forces a fresh entry so the next batch
			// can follow without requiring the sentinel to leave first.
			this._io.unobserve(this);
			this._io.observe(this);
		}
	}

	if (!customElements.get('infinite-reveal')) {
		customElements.define('infinite-reveal', InfiniteReveal);
	}

	/* --- Collection cards: swatch price updates -------------------------------
	   Impact already swaps card media and product URLs for swatches. Collection
	   cards add a small price surface, so update that at the same time without
	   altering the vanilla product-card component used elsewhere. */
	document.addEventListener('change', (event) => {
		const input = event.target.closest?.('.bs-collection-card__swatches input[data-variant-price]');
		if (!input) return;
		const card = input.closest('.bs-collection-card');
		const host = card?.querySelector('.bs-collection-card__price');
		if (!host) return;

		const price = input.getAttribute('data-variant-price');
		const compare = input.getAttribute('data-variant-compare-price');
		const list = document.createElement('price-list');
		list.className = 'price-list';

		const sale = document.createElement('sale-price');
		sale.className = 'text-subdued text-sm';
		sale.textContent = price;
		list.appendChild(sale);

		// Struck RRP beside the price (restored 2026-09-17) — regular ink on the
		// selling price, never the on-sale colour; mirrors snippets/price-list.liquid.
		if (compare) {
			const regular = document.createElement('compare-at-price');
			regular.className = 'text-subdued text-sm line-through';
			regular.textContent = compare;
			list.appendChild(regular);
		}

		host.replaceChildren(list);

	});

	/* --- Collection cards: two-image swipe carousel ---------------------------
	   A native scroll-snap viewport keeps touch interaction predictable and lets
	   the card's existing variant media replacement update both slides. The first
	   eligible mobile card gives a slow, held three-cycle partial reveal to teach
	   the gesture; any shopper interaction cancels it immediately. */
	class CollectionCardCarousel extends HTMLElement {
		connectedCallback() {
			if (this._abortController) return;
			this._abortController = new AbortController();
			this._viewport = this.querySelector('[data-carousel-viewport]');
			this._dots = Array.from(this.querySelectorAll('[data-carousel-slide]'));
			this._activeIndex = 0;
			this._hintCancelled = false;
			this._hintTimeouts = [];

			if (!this._viewport || this._dots.length < 2) return;

			const signal = this._abortController.signal;
			this._viewport.addEventListener('scroll', () => this._queuePositionUpdate(), { passive: true, signal });
			this._viewport.addEventListener('pointerdown', () => this._cancelHint(), { passive: true, signal });
			this._viewport.addEventListener('touchstart', () => this._cancelHint(), { passive: true, signal });

			this._dots.forEach((dot) => {
				dot.addEventListener('click', () => {
					this._cancelHint();
					this._goTo(parseInt(dot.dataset.carouselSlide, 10), true);
				}, { signal });
			});

			this.closest('.bs-collection-card')?.addEventListener('change', () => {
				this._cancelHint();
				this._goTo(0, false);
			}, { signal });

			this._requestHint();
		}

		disconnectedCallback() {
			this._cancelHint();
			this._abortController?.abort();
			this._abortController = null;
			if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
		}

		_queuePositionUpdate() {
			if (this._scrollFrame) return;
			this._scrollFrame = requestAnimationFrame(() => {
				this._scrollFrame = null;
				const width = this._viewport.clientWidth;
				if (!width) return;
				this._setActive(Math.round(this._viewport.scrollLeft / width));
			});
		}

		_setActive(index) {
			const next = Math.max(0, Math.min(index, this._dots.length - 1));
			if (next === this._activeIndex && this._dots[next]?.classList.contains('is-active')) return;
			this._activeIndex = next;
			this._dots.forEach((dot, dotIndex) => {
				const active = dotIndex === next;
				dot.classList.toggle('is-active', active);
				dot.setAttribute('aria-current', active ? 'true' : 'false');
			});

			const slides = this._viewport.children;
			Array.from(slides).forEach((slide, slideIndex) => {
				const active = slideIndex === next;
				slide.setAttribute('aria-hidden', active ? 'false' : 'true');
				slide.tabIndex = active ? 0 : -1;
			});
		}

		_goTo(index, smooth) {
			const left = this._viewport.clientWidth * index;
			this._viewport.scrollTo({ left, behavior: smooth && !reduced() ? 'smooth' : 'auto' });
			this._setActive(index);
		}

		_requestHint() {
			if (!this.hasAttribute('data-swipe-hint') || reduced() || !window.matchMedia('(max-width: 699px)').matches) return;
			if (document.querySelector('collection-card-carousel[data-swipe-hint]') !== this) return;

			const runWhenVisible = () => {
				if (window.__bsCollectionCardHintUsed) {
					this._hintObserver?.disconnect();
					return;
				}
				window.__bsCollectionCardHintUsed = true;
				this._hintObserver?.disconnect();
				this._playHint();
			};

			this._hintObserver = new IntersectionObserver((entries) => {
				if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.65)) {
					window.bsMark?.('swipe-hint');
					runWhenVisible();
				}
			}, { threshold: 0.65 });
			this._hintObserver.observe(this);
		}

		_playHint() {
			if (this._hintCancelled || !this._viewport.clientWidth) return;
			this.classList.add('is-hinting');
			const peek = Math.round(this._viewport.clientWidth * 0.44);
			const sequence = [
				[600, peek], [2050, 0],
				[3650, peek], [5100, 0],
				[6700, peek], [8150, 0]
			];

			sequence.forEach(([delay, left], index) => {
				this._hintTimeouts.push(window.setTimeout(() => {
					if (this._hintCancelled) return;
					this._animateHintTo(left, 800);
					if (index === sequence.length - 1) {
						this._hintTimeouts.push(window.setTimeout(() => {
							this.classList.remove('is-hinting');
							this._setActive(0);
						}, 900));
					}
				}, delay));
			});
		}

		_animateHintTo(target, duration) {
			if (this._hintFrame) cancelAnimationFrame(this._hintFrame);
			const start = this._viewport.scrollLeft;
			const distance = target - start;
			const startedAt = performance.now();
			const easeInOut = (progress) => progress < 0.5
				? 4 * progress * progress * progress
				: 1 - Math.pow(-2 * progress + 2, 3) / 2;

			const step = (now) => {
				if (this._hintCancelled) return;
				const progress = Math.min((now - startedAt) / duration, 1);
				this._viewport.scrollLeft = start + distance * easeInOut(progress);
				if (progress < 1) {
					this._hintFrame = requestAnimationFrame(step);
				} else {
					this._hintFrame = null;
				}
			};

			this._hintFrame = requestAnimationFrame(step);
		}

		_cancelHint() {
			this._hintCancelled = true;
			this._hintObserver?.disconnect();
			this._hintTimeouts?.forEach((timeout) => clearTimeout(timeout));
			this._hintTimeouts = [];
			if (this._hintFrame) cancelAnimationFrame(this._hintFrame);
			this._hintFrame = null;
			this.classList.remove('is-hinting');
		}
	}

	if (!customElements.get('collection-card-carousel')) {
		customElements.define('collection-card-carousel', CollectionCardCarousel);
	}

	/* --- Collection progressive loading ---------------------------------------
	   Fetch and append paginated section HTML as the shopper approaches the end.
	   Automation stops at the configured product limit (100); an explicit Load
	   More button handles everything after that so the footer remains reachable.
	   Vanilla pagination stays available when JavaScript is disabled. */
	/* Pagination must preserve native facets, hidden device context and aesthetic
	   OR filtering together. Count visible product nodes rather than Shopify's
	   absolute indexes, or sparse legacy collections can fetch forever. */
	class CollectionInfiniteScroll extends HTMLElement {
		connectedCallback() {
			this._target = document.querySelector(this.getAttribute('target'));
			this._button = this.querySelector('.bs-collection-loader__button');
			this._status = this.querySelector('.bs-collection-loader__status');
			this._sentinel = this.querySelector('.bs-collection-loader__sentinel');
			this._autoLimit = parseInt(this.getAttribute('auto-limit') || '100', 10);
			this._nextUrl = this._readNextUrl(this);
			this._pendingNodes = [];
			this._abortController = new AbortController();
			this._loading = false;
			applyCollectionAestheticFilter(this._target);
			// A single-aesthetic direct URL is about to be replaced by Shopify's
			// server-filtered section. Do not let the provisional unfiltered grid
			// start its own observer request in parallel.
			if (window.__blancCollectionAestheticHydrating) return;

			if (!this._target || !this._button || !this._sentinel || !this._nextUrl) return;

			this.classList.add('is-enhanced');
			this._button.addEventListener('click', () => this._load(false), { signal: this._abortController.signal });

			this._observer = new IntersectionObserver(
				(entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					// Past the automatic limit the observer no longer appends: it
					// prefetches the next Load more batch into _pendingNodes so the
					// click appends instantly (8,500 sessions/fortnight were dead-
					// clicking a button that had nothing ready — 2026-09-07 audit).
					if (this._productCount() >= this._autoLimit) this._prefetch();
					else this._load(true);
				},
				// 1600px lookahead: batches should finish appending while the loader
				// is still below the viewport — an in-view append shifts the loader
				// and footer and was the largest real-user CLS source on collections.
				{ rootMargin: '1600px 0px' }
			);
			this._observer.observe(this._sentinel);

			if (this._productCount() >= this._autoLimit) {
				this._showButton();
				return;
			}
		}

		disconnectedCallback() {
			this._observer?.disconnect();
			this._abortController?.abort();
		}

		_readNextUrl(scope) {
			return scope.querySelector('.pagination a[rel="next"]')?.href || null;
		}

		_productCount() {
			return Array.from(this._target.querySelectorAll('[data-product-index]')).filter((product) => !product.hidden).length;
		}

		_append(nodes) {
			const activeTags = collectionAestheticTags();
			nodes.forEach((node) => {
				if (node.hasAttribute?.('data-product-index')) node.hidden = !productMatchesCollectionAesthetics(node, activeTags);
			});
			const fragment = document.createDocumentFragment();
			nodes.forEach((node) => fragment.appendChild(node));
			this._target.appendChild(fragment);
			reconcileCollectionPromos(this._target);
			initCollectionPromoVideos(this._target);
		}

		_showButton() {
			this._button.hidden = false;
			// The observer stays alive to prefetch (see connectedCallback); it is
			// only disconnected when nothing more can be loaded.
			if (!this._nextUrl && !this._pendingNodes.length && !this._streams?.some((stream) => stream.url)) this._observer?.disconnect();
		}

		/* Fetch the next manual batch in the background without appending. */
		_prefetch() {
			if (this._loading || this._prefetching || this._pendingNodes.length >= 24) return;
			if (!this._nextUrl && !this._streams?.some((stream) => stream.url)) return;
			this._prefetching = true;
			this._load(false, { prefetch: true }).finally(() => { this._prefetching = false; });
		}

		/* Immediate feedback for a click that has to wait on the network: reserve
		   the grid space with placeholder cards so the tap visibly did something
		   and the appended batch does not shift the footer. */
		_showSkeletons(count) {
			this._removeSkeletons();
			const fragment = document.createDocumentFragment();
			for (let index = 0; index < count; index += 1) {
				const placeholder = document.createElement('div');
				placeholder.className = 'bs-card-skeleton';
				placeholder.setAttribute('aria-hidden', 'true');
				placeholder.innerHTML = '<span class="bs-card-skeleton__media"></span><span class="bs-card-skeleton__line"></span><span class="bs-card-skeleton__line bs-card-skeleton__line--short"></span>';
				fragment.appendChild(placeholder);
			}
			this._target.appendChild(fragment);
		}

		_removeSkeletons() {
			this._target.querySelectorAll('.bs-card-skeleton').forEach((node) => node.remove());
		}

		_rearmObserver() {
			if (!this._observer) return;
			this._observer.unobserve(this._sentinel);
			this._observer.observe(this._sentinel);
		}

		_showingLabel() {
			// Locale-file label travels via data attribute; {count} is replaced here.
			return (this.dataset.labelShowing || 'Showing [count] products').replace('[count]', this._productCount());
		}

		async _load(automatic, options = {}) {
			const prefetch = !!options.prefetch;
			window.bsMark?.(prefetch ? 'load-more-prefetch' : 'load-more');
			if (this._loading) {
				// A click while the background prefetch is still in flight must not
				// be swallowed: show the loading state now and append the batch the
				// moment the prefetch lands (see the prefetch epilogue).
				if (!automatic && !prefetch && this._prefetching && !this._appendAfterPrefetch) {
					this._appendAfterPrefetch = true;
					this.classList.add('is-loading');
					this.setAttribute('aria-busy', 'true');
					this._button.disabled = true;
					this._status.textContent = this.dataset.labelLoadingStatus || 'Loading more products…';
					this._button.dataset.originalLabel = this._button.textContent;
					this._button.textContent = this.dataset.labelLoading || 'Loading…';
					this._showSkeletons(8);
				}
				return;
			}
			// Journey tracking: how long a Load more / auto batch takes and how many
			// section requests it needed (PostHog `collection_load_more`).
			const loadStartedAt = performance.now();
			const productsBefore = this._productCount();
			this._requestCount = 0;
			const moreAvailable = () => !!this._nextUrl || !!this._streams?.some((stream) => stream.url);

			// After a manual click the shopper has said "keep going": raise the
			// automatic limit by four pages so scrolling streams the next batches
			// and the button only reappears at the new limit.
			if (!automatic && !prefetch) this._autoLimit = Math.max(this._autoLimit, this._productCount() + 96);

			// The automatic path drains a prefetched batch before fetching again
			// (its pages are already past _nextUrl; fetching would duplicate).
			if (automatic && this._pendingNodes.length) {
				this._append(this._pendingNodes.splice(0));
				this._status.textContent = this._showingLabel();
				if (this._productCount() >= this._autoLimit) this._showButton();
				else if (moreAvailable()) this._rearmObserver();
				return;
			}

			// A click with a prefetched batch waiting appends it instantly, then
			// starts prefetching the next one. (Legacy: pending nodes with nothing
			// left to fetch behave the same way.)
			if (!automatic && !prefetch && this._pendingNodes.length >= 24) {
				this._append(this._pendingNodes.splice(0));
				this._status.textContent = this._showingLabel();
				window.bsTrack?.('collection_load_more', {
					mode: 'manual_prefetched',
					products_before: productsBefore,
					products_after: this._productCount(),
					requests: 0,
					duration_ms: 0,
					aesthetics: collectionAestheticTags().length,
				});
				if (moreAvailable() || this._pendingNodes.length) {
					this._button.hidden = this._productCount() < this._autoLimit;
					this._rearmObserver();
				} else {
					this._button.hidden = true;
					this._observer?.disconnect();
				}
				return;
			}
			if (!automatic && !prefetch && this._pendingNodes.length && !moreAvailable()) {
				this._append(this._pendingNodes.splice(0));
				this._status.textContent = this._showingLabel();
				this._button.hidden = true;
				return;
			}

			if (!moreAvailable()) {
				if (!prefetch) this._button.hidden = true;
				this._observer?.disconnect();
				return;
			}

			this._loading = true;
			const originalLabel = this._button.textContent;
			if (!prefetch) {
				this.classList.add('is-loading');
				this.setAttribute('aria-busy', 'true');
				this._button.disabled = true;
				this._status.textContent = this.dataset.labelLoadingStatus || 'Loading more products…';
				this._button.textContent = this.dataset.labelLoading || 'Loading…';
				if (!automatic) this._showSkeletons(8);
			}

			try {
				const section = this.closest('.shopify-section');
				const sectionId = section?.id.replace('shopify-section-', '');
				const collectionElement = section?.querySelector('.collection');
				const contextModel = window.BlancSpaceDevicePreference?.getVisualModel?.() || collectionElement?.dataset.contextPhoneModel || '';
				const phoneFilterParam = collectionElement?.dataset.phoneFilterParam || '';
				const activeTags = collectionAestheticTags();
				let remainingProducts = Math.max(this._autoLimit - this._productCount(), 0);
				// An explicit Load More click should feel decisive: gather roughly two
				// Shopify pages (~48 products) per press instead of one 24-product page.
				const MANUAL_BATCH = 48;
				let manualGained = 0;
				const visible = [];
				const pending = [];

				if (!automatic && this._pendingNodes.length) {
					const drained = this._pendingNodes.splice(0);
					visible.push(...drained);
					manualGained += drained.filter((node) => node.hasAttribute?.('data-product-index')).length;
				}

				// Two or more aesthetics previously scanned every unfiltered Shopify
				// page serially looking for OR matches — 10-20s+ on 2,000-product
				// collections (24 products per request). Shopify's legacy `constraint`
				// param still tag-filters `collection.products` server-side with real
				// pagination (the same transport the single-aesthetic mode uses), so
				// fetch one small server-filtered stream per tag and dedupe by product
				// handle instead. Falls through to the epilogue with the same
				// visible/pending accumulators as the scan path.
				if (activeTags.length >= 2) {
					if (!this._streams) {
						this._streams = activeTags.map((tag) => {
							const streamUrl = new URL(this._nextUrl, location.href);
							streamUrl.searchParams.delete('filter.p.tag');
							streamUrl.searchParams.set('constraint', tag);
							streamUrl.searchParams.set('page', '1');
							return { url: streamUrl.toString() };
						});
					}
					const seenHandles = new Set(
						Array.from(this._target.querySelectorAll('[data-product-index]'))
							.map((node) => node.getAttribute('handle'))
							.filter(Boolean)
					);
					this._pendingNodes.forEach((node) => {
						const handle = node.getAttribute?.('handle');
						if (handle) seenHandles.add(handle);
					});
					const seenPromos = new Set(
						Array.from(this._target.querySelectorAll('[data-promo-position]')).map((node) => node.dataset.promoPosition)
					);
					visible.forEach((node) => {
						const handle = node.getAttribute?.('handle');
						if (handle) seenHandles.add(handle);
						if (node.dataset?.promoPosition) seenPromos.add(node.dataset.promoPosition);
					});

					for (const stream of this._streams) {
						while (stream.url && (automatic ? remainingProducts > 0 : manualGained < MANUAL_BATCH)) {
							const url = new URL(stream.url, location.href);
							if (sectionId) url.searchParams.set('section_id', sectionId);
							if (contextModel) {
								if (phoneFilterParam) {
									url.searchParams.delete(phoneFilterParam);
									url.searchParams.append(phoneFilterParam, contextModel);
								}
								url.searchParams.set('_bs_device_context', contextModel);
							}

							this._requestCount += 1;
							const response = await fetch(url.toString(), { signal: this._abortController.signal });
							if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);
							const page = new DOMParser().parseFromString(await response.text(), 'text/html');
							const nextGrid = page.querySelector('product-list');
							if (!nextGrid) throw new Error('Collection page did not contain a product grid');
							const nextLoader = page.querySelector('collection-infinite-scroll');
							stream.url = nextLoader ? this._readNextUrl(nextLoader) : null;

							Array.from(nextGrid.children).forEach((node) => {
								if (node.hasAttribute('data-product-index')) {
									// Every stream product matches its tag server-side; only
									// dedupe (initial page overlap, products carrying both tags).
									const handle = node.getAttribute('handle');
									if (handle) {
										if (seenHandles.has(handle)) return;
										seenHandles.add(handle);
									}
									if (!automatic) {
										visible.push(node);
										manualGained += 1;
									} else if (remainingProducts > 0) {
										visible.push(node);
										remainingProducts -= 1;
									} else {
										pending.push(node);
									}
								} else if (node.hasAttribute('data-promo-position')) {
									if (seenPromos.has(node.dataset.promoPosition)) return;
									seenPromos.add(node.dataset.promoPosition);
									visible.push(node);
								}
							});
						}
						if (automatic ? remainingProducts <= 0 : manualGained >= MANUAL_BATCH) break;
					}

					// _nextUrl is never fetched again in stream mode; it survives only
					// as the "more available" flag the epilogue and manual path check.
					if (!this._streams.some((stream) => stream.url)) this._nextUrl = null;
				} else
				// Shopify treats repeated tag facets as AND. Our aesthetic control is
				// intentionally OR, so filtered collections may need several Shopify
				// pages to find a handful of matching designs. Scan those pages under
				// one stable loading state and append the matches as one batch rather
				// than flashing the loader for every individual product found.
				do {
					const url = new URL(this._nextUrl, location.href);
					if (sectionId) url.searchParams.set('section_id', sectionId);
					if (contextModel) {
						// Shopify must receive the real option filter on every paginated
						// section request. _bs_device_context only separates cached URLs;
						// Liquid cannot read it and previously fell back to a stale cart
						// model, discarding every otherwise-valid Impact Air card.
						if (phoneFilterParam) {
							url.searchParams.delete(phoneFilterParam);
							url.searchParams.append(phoneFilterParam, contextModel);
						}
						url.searchParams.set('_bs_device_context', contextModel);
					}

					// A manual/prefetch batch on an unfiltered grid needs two Shopify
					// pages (24 products each). Fetch the pair concurrently instead of
					// serially: the second page's URL is the first's with page+1, and
					// its own rel=next tells us whether a third exists.
					const pairUrls = [url];
					if (!automatic && !activeTags.length) {
						const currentPage = parseInt(url.searchParams.get('page') || '1', 10);
						const second = new URL(url.toString());
						second.searchParams.set('page', String(currentPage + 1));
						pairUrls.push(second);
					}
					this._requestCount += pairUrls.length;
					const responses = await Promise.all(pairUrls.map((pairUrl) => fetch(pairUrl.toString(), { signal: this._abortController.signal })));
					if (!responses[0].ok) throw new Error(`Collection page request failed: ${responses[0].status}`);
					const pages = [];
					for (const response of responses) {
						if (!response.ok) break;
						const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
						if (!parsed.querySelector('product-list')) break;
						pages.push(parsed);
					}
					if (!pages.length) throw new Error('Collection page did not contain a product grid');

					const lastPage = pages[pages.length - 1];
					const nextLoader = lastPage.querySelector('collection-infinite-scroll');
					// The pair's own pagination is authoritative; a missing second page
					// (404/no grid) means the first page's rel=next was the end.
					this._nextUrl = nextLoader ? this._readNextUrl(nextLoader) : null;
					const nodes = pages.flatMap((parsed) => Array.from(parsed.querySelector('product-list').children));

					if (!automatic) {
						visible.push(...nodes);
						const activeManualTags = collectionAestheticTags();
						manualGained += nodes.filter(
							(node) => node.hasAttribute?.('data-product-index') && productMatchesCollectionAesthetics(node, activeManualTags)
						).length;
						continue;
					}

					nodes.forEach((node) => {
						const isProduct = node.hasAttribute('data-product-index');
						const isPromo = node.hasAttribute('data-promo-position');
						const isMatchingProduct = isProduct && productMatchesCollectionAesthetics(node, activeTags);

						if (isMatchingProduct && remainingProducts > 0) {
							visible.push(node);
							remainingProducts -= 1;
						} else if (isMatchingProduct) {
							pending.push(node);
						} else if (isPromo) {
							visible.push(node);
						} else if (!activeTags.length && remainingProducts > 0) {
							// Preserve non-product extension nodes on ordinary collection
							// pages. Non-matching aesthetic cards are deliberately discarded.
							visible.push(node);
						}
					});
				} while (
					automatic
						? activeTags.length && this._nextUrl && remainingProducts > 0
						: this._nextUrl && manualGained < MANUAL_BATCH
				);

				if (prefetch) {
					// Hold the batch for the next click; nothing on screen changes —
					// unless a click already arrived while this prefetch was running.
					this._pendingNodes.push(...visible, ...pending);
					window.bsTrack?.('collection_load_more', {
						mode: 'prefetch',
						products_before: productsBefore,
						products_after: productsBefore,
						requests: this._requestCount,
						duration_ms: Math.round(performance.now() - loadStartedAt),
						aesthetics: collectionAestheticTags().length,
					});
					if (this._appendAfterPrefetch) {
						this._appendAfterPrefetch = false;
						this._removeSkeletons();
						this._append(this._pendingNodes.splice(0));
						this._status.textContent = this._showingLabel();
						this.classList.remove('is-loading');
						this.removeAttribute('aria-busy');
						this._button.disabled = false;
						this._button.textContent = this._button.dataset.originalLabel || originalLabel;
						window.bsTrack?.('collection_load_more', {
							mode: 'manual_awaited_prefetch',
							products_before: productsBefore,
							products_after: this._productCount(),
							requests: this._requestCount,
							duration_ms: Math.round(performance.now() - loadStartedAt),
							aesthetics: collectionAestheticTags().length,
						});
						if (moreAvailable()) {
							this._showButton();
							this._rearmObserver();
							setTimeout(() => this._prefetch(), 0);
						} else {
							this._button.hidden = true;
							this._observer?.disconnect();
						}
						return;
					}
					if (moreAvailable() || this._pendingNodes.length) this._showButton();
					else this._button.hidden = true;
					return;
				}

				this._removeSkeletons();
				this._append(visible);
				this._pendingNodes = pending;

				this._status.textContent = this._showingLabel();
				window.bsTrack?.('collection_load_more', {
					mode: automatic ? 'auto' : 'manual',
					products_before: productsBefore,
					products_after: this._productCount(),
					requests: this._requestCount,
					duration_ms: Math.round(performance.now() - loadStartedAt),
					aesthetics: collectionAestheticTags().length,
				});

				if (this._productCount() >= this._autoLimit) {
					if (this._pendingNodes.length || moreAvailable()) {
						this._showButton();
						// Warm the next batch so the following click is instant too.
						this._rearmObserver();
						if (!automatic) this._prefetch();
					} else {
						this._button.hidden = true;
						this._observer?.disconnect();
					}
				} else if (moreAvailable()) {
					this._rearmObserver();
				} else {
					this._observer?.disconnect();
					this._button.hidden = true;
				}
			} catch (error) {
				this._removeSkeletons();
				if (this._appendAfterPrefetch) {
					this._appendAfterPrefetch = false;
					this.classList.remove('is-loading');
					this.removeAttribute('aria-busy');
					this._button.disabled = false;
					this._button.textContent = this._button.dataset.originalLabel || originalLabel;
				}
				if (error.name !== 'AbortError' && !prefetch) {
					this._status.textContent = this.dataset.labelLoadError || 'More products could not be loaded. Please try again.';
					this._showButton();
				}
			} finally {
				this._loading = false;
				if (!prefetch) {
					this.classList.remove('is-loading');
					this.removeAttribute('aria-busy');
					this._button.disabled = false;
					this._button.textContent = originalLabel;
				}
			}
		}
	}

	if (!customElements.get('collection-infinite-scroll')) {
		customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
	}

	/* Collection promo and editorial feature videos do not download until their
	   tile approaches the viewport. This keeps deep media out of initial loads. */
	const initCollectionPromoVideos = (scope = document) => {
		const selector = 'video[data-bs-promo-video]:not([data-bs-promo-video-observed])';
		const videos = [];

		if (scope instanceof Element && scope.matches(selector)) videos.push(scope);
		videos.push(...scope.querySelectorAll(selector));
		if (!videos.length) return;

		const loadVideo = (video) => {
			window.bsMark?.('promo-video');
			video.dataset.bsPromoVideoReady = 'true';
			video.querySelectorAll('source[data-src]').forEach((source) => {
				source.src = source.dataset.src;
				source.removeAttribute('data-src');
			});
			video.load();
			if (!reduced()) video.play().catch(() => {});
		};

		if (!('IntersectionObserver' in window)) {
			videos.forEach(loadVideo);
			return;
		}

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				observer.unobserve(entry.target);
				loadVideo(entry.target);
			});
		}, { rootMargin: '200px 0px' });

		videos.forEach((video) => {
			video.dataset.bsPromoVideoObserved = 'true';
			observer.observe(video);
		});
	};

	initCollectionPromoVideos();

	/* Track123 inserts unlabelled form inputs and a decorative mobile banner.
	   Normalise the app-owned nodes after asynchronous renders so its core
	   order-search journey remains usable to screen-reader customers. */
	const normalizeTrack123Accessibility = (scope = document) => {
		const selector = '.track123-mobile-banner > img:not([alt])';
		if (scope instanceof Element && scope.matches(selector)) scope.alt = '';
		scope.querySelectorAll(selector).forEach((image) => { image.alt = ''; });

		const inputs = [];
		if (scope instanceof Element && scope.matches('.track123-input:not([aria-label]):not([aria-labelledby])')) inputs.push(scope);
		inputs.push(...scope.querySelectorAll('.track123-input:not([aria-label]):not([aria-labelledby])'));
		inputs.forEach((input) => {
			const label = input.closest('.track123-input-content')?.querySelector('.track123-input-label');
			if (!label?.textContent.trim()) return;
			const labelKey = label.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
			label.id ||= `bs-track123-label-${labelKey}`;
			input.setAttribute('aria-labelledby', label.id);
		});
	};

	normalizeTrack123Accessibility();
	/* The collection description sits below the grid (sections/main-collection.liquid moves it out of the
	   header's <template data-bs-description-below> before first paint). Every facet / device-context / model
	   refresh replaces the whole main-collection section with a fetched fragment: its target is empty and its
	   inline script never runs (DOMParser scripts are inert), so re-seed it from the template, which stays in
	   the header section for exactly this reason. Found 14 Oct 2026: the first-visit device-context refresh
	   had been wiping the description on every case collection on live. */
	const reseedCollectionDescription = (scope) => {
		const target = scope.matches?.('[data-bs-description-below-target]') ? scope : scope.querySelector?.('[data-bs-description-below-target]');
		if (!target || target.children.length) return;
		const template = document.querySelector('template[data-bs-description-below]');
		if (template) target.appendChild(template.content.cloneNode(true));
	};
	document.addEventListener('shopify:section:load', (event) => { initCollectionPromoVideos(event.target); reseedCollectionDescription(event.target); });
	new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (!(node instanceof Element)) return;
				reseedCollectionDescription(node);
				if (node.matches('[data-bs-promo-video]') || node.querySelector('[data-bs-promo-video]')) initCollectionPromoVideos(node);
				if (node.matches('.track123-input, .track123-mobile-banner > img') || node.querySelector('.track123-input, .track123-mobile-banner > img')) normalizeTrack123Accessibility(node);
			});
		});
	}).observe(document.documentElement, { childList: true, subtree: true });

	/* Shop-by-phone collections own the model for that page. They may infer the
	   site preference until the shopper explicitly locks a device in the menu;
	   after that, visiting another model collection does not silently replace it. */
	const lockedCollection = document.querySelector('.collection[data-locked-phone-model]');
	if (lockedCollection) {
		window.BlancSpaceDevicePreference?.set(lockedCollection.dataset.lockedPhoneModel, { source: 'collection' });
		const url = new URL(location.href);
		const hasVisibleFacet = Array.from(url.searchParams.keys()).some((key) => key.startsWith('filter.'));
		const sectionId = lockedCollection.closest('.shopify-section')?.id.replace('shopify-section-', '');
		if (hasVisibleFacet && sectionId) {
			url.searchParams.set('section_id', sectionId);
			document.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
		}
	}

	/* The initial server render computes filter counts without any device
	   context (clean customer URLs carry no filter params), so Shopify counts
	   case types across every variant — a Samsung page happily offers
	   "Impact Air" even though no Samsung variant has it, and clicking it
	   builds a guaranteed zero-result page. Re-request the section once, off
	   the critical path, with the real phone filter applied; the drawer's
	   Liquid hides zero-count case types, so swapping in the recomputed
	   options removes dead choices for the visitor's device. */
	const refreshFilterAvailability = async () => {
		const drawerOptions = document.querySelector('#facet-form [data-bs-filter-options]');
		const contextCollection = document.querySelector('.collection[data-locked-phone-model], .collection[data-phone-filter-param]');
		if (!drawerOptions || !contextCollection) return;
		const sectionId = contextCollection.closest('.shopify-section')?.id.replace('shopify-section-', '');
		const filterParam = contextCollection.dataset.lockedPhoneFilterParam || contextCollection.dataset.phoneFilterParam;
		if (!sectionId || !filterParam) return;
		if (new URL(location.href).searchParams.has(filterParam)) return; // counts already device-aware

		try {
			await window.BlancSpaceDevicePreference?.visualReady;
		} catch (error) {
			// Detection failing just means we keep the server-rendered options.
		}
		const model =
			contextCollection.dataset.lockedPhoneModel ||
			window.BlancSpaceDevicePreference?.getVisualModel?.() ||
			contextCollection.dataset.contextPhoneModel ||
			'';
		if (!model) return;

		// The swap must never happen under the shopper's fingers: an OPEN drawer
		// (staged or not) keeps its options until it closes — focus and reading
		// position survive, and the refreshed set lands on the next open.
		const drawerIsOpen = () =>
			Boolean(drawerOptions.closest('[open]') || document.querySelector('#facets-drawer[open], facet-dialog[open]'));

		const run = async () => {
			// The shopper beat us to the drawer: never discard staged choices.
			if (drawerOptions.querySelector('input:checked') || drawerOptions.isConnected === false) return;
			try {
				const url = new URL(location.href);
				url.searchParams.set('section_id', sectionId);
				url.searchParams.append(filterParam, model);
				window.bsMark?.('filter-refresh-fetch');
				const response = await fetch(url.toString());
				if (!response.ok) return;
				const html = await response.text();
				// The response is the whole collection section (~260 KB, 40+ cards with
				// srcsets). Parse it in an idle slot so it never lands on a tap.
				await new Promise((resolve) => {
					if ('requestIdleCallback' in window) requestIdleCallback(resolve, { timeout: 3000 });
					else setTimeout(resolve, 100);
				});
				window.bsMark?.('filter-refresh-parse');
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const incoming = doc.querySelector('#facet-form [data-bs-filter-options]');
				const applySwap = () => {
					if (!incoming || drawerOptions.querySelector('input:checked') || !drawerOptions.isConnected) return;
					drawerOptions.replaceWith(document.importNode(incoming, true));
				};
				if (drawerIsOpen()) {
					// Re-check every second until the drawer closes, then swap.
					const waitForClose = setInterval(() => {
						if (drawerIsOpen()) return;
						clearInterval(waitForClose);
						applySwap();
					}, 1000);
					setTimeout(() => clearInterval(waitForClose), 120000);
				} else {
					applySwap();
				}
			} catch (error) {
				// Network hiccups keep the (over-inclusive) server options — safe.
			}
		};
		// This re-render is the whole collection section (~250 KB) on every
		// collection view, so it now runs on filtering intent (first touch/hover/
		// focus on a toolbar pill or the drawer opening) or, for shoppers who
		// linger, after six seconds — never for a bounce or a quick browse
		// (2026-09-07 audit: the second section fetch on every load).
		let scheduled = false;
		const runOnce = () => {
			if (scheduled) return;
			scheduled = true;
			run();
		};
		const toolbar = document.querySelector('.bs-collection-toolbar');
		['pointerenter', 'pointerdown', 'touchstart', 'focusin'].forEach((name) =>
			toolbar?.addEventListener(name, runOnce, { capture: true, once: true, passive: true })
		);
		document.addEventListener('dialog:after-show', (event) => {
			if (event.target?.id === 'facets-drawer' || event.target?.tagName === 'FACET-DIALOG' || event.target?.tagName === 'FACET-DRAWER') runOnce();
		}, true);
		// A page restored from the Back cache resumes a pending timer at once, i.e.
		// exactly when the shopper is tapping the next card (iPhone back-return taps
		// went 192 → 832 ms p75 after this timer shipped on 2026-09-08). Once the
		// page has been restored, the refresh waits for filtering intent only.
		let restoredFromCache = false;
		window.addEventListener('pageshow', (event) => { if (event.persisted) restoredFromCache = true; });
		if (navigator.connection?.saveData !== true) {
			setTimeout(() => { if (!restoredFromCache && document.visibilityState === 'visible') runOnce(); }, 6000);
		}
	};
	refreshFilterAvailability();

	/* General collections render from the saved cart attribute, so ordinary
	   navigations no longer need the old browser-only hidden-facet refresh. This
	   one replacement is retained only for existing visitors whose localStorage
	   preference has not yet been copied into their cart. It also cleans old
	   visible Phone Model facet URLs before they can conflict with that saved
	   server context. */
	let collectionContextRefreshPromise = null;
	const refreshCollectionContext = async () => {
		if (collectionContextRefreshPromise) return collectionContextRefreshPromise;
		const collectionElement = document.querySelector('.collection:not([data-locked-phone-model]):not([data-generic-collection])');
		const sectionId = collectionElement?.closest('.shopify-section')?.id.replace('shopify-section-', '');
		if (!collectionElement || !sectionId) return;

		collectionContextRefreshPromise = (async () => {
			await window.BlancSpaceDevicePreference?.ready;
			await window.BlancSpaceDevicePreference?.visualReady;
			const url = new URL(location.href);
			const model = window.BlancSpaceDevicePreference?.getVisualModel?.() || '';
			if (model) url.searchParams.set('_bs_device_context', model);
			url.searchParams.set('section_id', sectionId);
			document.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
		})().finally(() => {
			collectionContextRefreshPromise = null;
		});

		return collectionContextRefreshPromise;
	};

	if (!lockedCollection) {
		const collectionElement = document.querySelector('.collection:not([data-generic-collection])');
		const browserContextModel = window.BlancSpaceDevicePreference?.getVisualModel?.() || '';
		const renderedContextModel = collectionElement?.dataset.contextPhoneModel || '';
		const phoneFilterParam = collectionElement?.dataset.phoneFilterParam || '';
		const hasLegacyPhoneFilter = phoneFilterParam && new URL(location.href).searchParams.has(phoneFilterParam);
		if (collectionElement && (hasLegacyPhoneFilter || (browserContextModel && browserContextModel !== renderedContextModel))) {
			refreshCollectionContext();
		}
	}

	window.addEventListener('blancspace:phone-model-change', (event) => {
		// Menu selection immediately navigates to the chosen shop-by-phone page;
		// avoid starting a redundant section request underneath that navigation.
		// The newsletter popup and the collection drawer's model picker are the
		// exceptions: both stay on the page, so their menu-weight picks must
		// rerender the grid in place.
		const staysOnPage = event.detail?.origin === 'popup' || event.detail?.origin === 'collection';
		const menuNavigationPending = event.detail?.source === 'menu' && !staysOnPage;
		if (!lockedCollection && !document.querySelector('.collection[data-generic-collection]') && !menuNavigationPending) refreshCollectionContext();
	});

	/* --- Collection model picker + toolbar model pill ---------------------------
	   The filter drawer's phone-model buttons (collection-model-options.liquid)
	   are explicit choices like the menu picker. On a shop-by-phone page the
	   model IS the page, so a different model navigates to its collection; on
	   a general collection the grid re-renders in place via the handler above.
	   The toolbar pill just opens the drawer with the model group expanded. */
	document.addEventListener('click', async (event) => {
		const option = event.target.closest?.('[data-bs-model-option]');
		if (!option) return;
		const model = option.dataset.model || '';
		if (!model || option.getAttribute('aria-busy') === 'true') return;

		const collectionElement = document.querySelector('.collection');
		const lockedModel = collectionElement?.dataset.lockedPhoneModel || '';
		option.setAttribute('aria-busy', 'true');

		if (lockedModel && model !== lockedModel && option.dataset.url) {
			// Navigate at once: set() writes localStorage synchronously and mirrors
			// the cart with a keepalive POST that completes during the navigation,
			// and the destination is that model's own locked collection, so its
			// render never depends on the attribute. Awaiting the POST added its
			// round trip to the tap (same change as the header menu picker).
			try { window.BlancSpaceDevicePreference?.set?.(model, { source: 'menu', force: true, origin: 'collection' })?.catch?.(() => {}); } catch (error) { /* navigation still applies the model */ }
			window.location.assign(option.dataset.url);
			return;
		}

		// Close the drawer before the section (containing this drawer) re-renders.
		document.getElementById('facets-drawer')?.hide?.();
		try { await window.BlancSpaceDevicePreference?.set?.(model, { source: 'menu', force: true, origin: 'collection' }); } catch (error) { /* keep the previous context */ }
		option.removeAttribute('aria-busy');
	});

	// The drawer's device pill reveals/hides the model buttons.
	document.addEventListener('click', (event) => {
		const toggle = event.target.closest?.('[data-bs-model-toggle]');
		if (!toggle) return;
		const list = toggle.parentElement?.querySelector('[data-bs-model-filter]');
		if (!list) return;
		list.hidden = !list.hidden;
		toggle.setAttribute('aria-expanded', String(!list.hidden));
	});

	// The drawer DOM persists between opens — start each visit collapsed.
	document.addEventListener('dialog:after-hide', (event) => {
		if (event.target?.id !== 'facets-drawer') return;
		const toggle = event.target.querySelector('[data-bs-model-toggle]');
		const list = event.target.querySelector('[data-bs-model-filter]');
		if (list && !list.hidden) {
			list.hidden = true;
			toggle?.setAttribute('aria-expanded', 'false');
		}
	}, true);

	document.addEventListener('click', (event) => {
		if (!event.target.closest?.('[data-bs-model-pill]')) return;
		// The vendor aria-controls wiring opens the drawer; greet the shopper
		// with the model list already revealed.
		setTimeout(() => {
			const toggle = document.querySelector('#facets-drawer [data-bs-model-toggle]');
			const list = toggle?.parentElement?.querySelector('[data-bs-model-filter]');
			if (list?.hidden) {
				list.hidden = false;
				toggle.setAttribute('aria-expanded', 'true');
			}
		}, 50);
	});

	window.addEventListener('blancspace:visual-phone-model-change', () => {
		if (!lockedCollection) refreshCollectionContext();
	});

	/* --- Pinterest grid: keyed tiles + auto pastel tints -------------------------
	   For the "Pastel tint" tile style each render is processed once on a
	   canvas (CORS re-fetch, cached per URL):

	   1. Flood-fill from the image borders through near-white/neutral pixels —
	      everything reachable from outside is true background + drop shadow;
	      the camera module sits inside the phone silhouette and is never
	      touched (a value threshold alone cannot make that distinction).
	   2. Background pixels go transparent; shadow pixels become black at
	      (1 - luminance) alpha, so the shadow naturally darkens whatever
	      pastel is underneath — a real blend, unlike the white-key filter.
	   3. The phone's bounding box is re-framed with an even margin on the
	      tile's 3:5 canvas, fixing the renders' uneven baked-in padding.
	   4. The dominant saturated hue (interior pixels only) sets the tile's
	      pastel via --pin-tint; designs with no real colour stay neutral.

	   The processed image replaces the <img> src; until then the SVG
	   white-key filter approximates the look. */

	const initPinTints = () => {
		if (!document.querySelector('.pinterest-grid--tint')) return;

		const MARGIN = 0.08; // even inner margin, fraction of output width
		const ASPECT = 3 / 5; // output canvas w/h — must match the tint tiles' CSS aspect-ratio
		const OUT_W = 600;
		const processed = new WeakSet();
		const cache = new Map(); // src -> Promise<{url, tint} | null>

		const keyAndCrop = (img) => {
			const w = img.naturalWidth;
			const h = img.naturalHeight;
			const src = document.createElement('canvas');
			src.width = w;
			src.height = h;
			const sctx = src.getContext('2d', { willReadFrequently: true });
			sctx.drawImage(img, 0, 0);
			let image;
			try {
				image = sctx.getImageData(0, 0, w, h);
			} catch (e) {
				return null; // tainted (no CORS) — leave the filter fallback in place
			}
			const d = image.data;
			const n = w * h;

			// A pixel the flood can flow through: light and neutral (bg/shadow)
			const passable = (i) => {
				const r = d[i * 4];
				const g = d[i * 4 + 1];
				const b = d[i * 4 + 2];
				const mx = Math.max(r, g, b);
				const mn = Math.min(r, g, b);
				return mx - mn <= 26 && r + g + b >= 585; // neutral, lum >= ~195
			};

			// Flood-fill from every border pixel
			const outside = new Uint8Array(n);
			const stack = [];
			for (let x = 0; x < w; x++) {
				if (passable(x)) stack.push(x);
				const bot = (h - 1) * w + x;
				if (passable(bot)) stack.push(bot);
			}
			for (let y = 0; y < h; y++) {
				const left = y * w;
				if (passable(left)) stack.push(left);
				const right = y * w + w - 1;
				if (passable(right)) stack.push(right);
			}
			while (stack.length) {
				const i = stack.pop();
				if (outside[i]) continue;
				outside[i] = 1;
				const x = i % w;
				if (x > 0 && !outside[i - 1] && passable(i - 1)) stack.push(i - 1);
				if (x < w - 1 && !outside[i + 1] && passable(i + 1)) stack.push(i + 1);
				if (i >= w && !outside[i - w] && passable(i - w)) stack.push(i - w);
				if (i < n - w && !outside[i + w] && passable(i + w)) stack.push(i + w);
			}

			// Invasion check: on clear cases the light bumper lets the flood
			// leak INSIDE the phone, turning interior pockets translucent
			// (blotchy). A solid-bumper render never floods its own centre,
			// so outside pixels in the central window mean this image can't
			// be keyed cleanly — sample the tint but skip the pixel rewrite.
			const cx0 = Math.floor(w * 0.35);
			const cx1 = Math.ceil(w * 0.65);
			const cy0 = Math.floor(h * 0.35);
			const cy1 = Math.ceil(h * 0.65);
			let coreOutside = 0;
			for (let y = cy0; y < cy1; y++) {
				for (let x = cx0; x < cx1; x++) {
					if (outside[y * w + x]) coreOutside++;
				}
			}
			const invaded = coreOutside / ((cx1 - cx0) * (cy1 - cy0)) > 0.05;

			// Rewrite outside pixels as alpha shadow; find the content bbox
			// (interior + any visible shadow, so soft shadows stay in frame).
			let minX = w;
			let minY = h;
			let maxX = -1;
			let maxY = -1;
			const buckets = Array.from({ length: 12 }, () => ({ w: 0, x: 0, y: 0 }));
			let saturated = 0;
			for (let i = 0; i < n; i++) {
				const p = i * 4;
				if (outside[i]) {
					if (invaded) {
						// No pixel rewrite for invaded (clear-case) renders, but
						// shadow-dark background still frames the crop so the
						// re-framed tile keeps its shadow.
						const lumI = (d[p] + d[p + 1] + d[p + 2]) / 3;
						if (lumI > 243) continue;
					} else {
						const lum = (d[p] + d[p + 1] + d[p + 2]) / 3;
						const alpha = Math.max(0, Math.round(255 - lum));
						d[p] = 0;
						d[p + 1] = 0;
						d[p + 2] = 0;
						d[p + 3] = alpha;
						if (alpha < 12) continue; // invisible — don't let it grow the bbox
					}
				} else {
					// Interior pixel: contributes to the dominant-hue sample
					const r = d[p] / 255;
					const g = d[p + 1] / 255;
					const b = d[p + 2] / 255;
					const mx = Math.max(r, g, b);
					const mn = Math.min(r, g, b);
					const l = (mx + mn) / 2;
					const dd = mx - mn;
					if (dd >= 0.08 && l >= 0.15 && l <= 0.92) {
						const s = dd / (1 - Math.abs(2 * l - 1));
						if (s >= 0.2) {
							let hue;
							if (mx === r) hue = ((g - b) / dd + 6) % 6;
							else if (mx === g) hue = (b - r) / dd + 2;
							else hue = (r - g) / dd + 4;
							hue *= 60;
							saturated++;
							const bucket = buckets[Math.floor(hue / 30) % 12];
							bucket.w += s;
							bucket.x += Math.cos((hue * Math.PI) / 180) * s;
							bucket.y += Math.sin((hue * Math.PI) / 180) * s;
						}
					}
				}
				const x = i % w;
				const y = (i / w) | 0;
				if (x < minX) minX = x;
				if (x > maxX) maxX = x;
				if (y < minY) minY = y;
				if (y > maxY) maxY = y;
			}
			if (maxX <= minX || maxY <= minY) return null;

			let tint = null;
			// 5%: low enough that any genuinely colourful design qualifies,
			// high enough that sparkle glints on b/w designs don't invent one.
			if (saturated / n >= 0.05) {
				const best = buckets.reduce((a, b) => (b.w > a.w ? b : a));
				if (best.w) {
					const hue = Math.round(((Math.atan2(best.y, best.x) * 180) / Math.PI + 360) % 360);
					// A clear step darker than the page background so the cards
					// read by lightness alone (colour-vision-safe), not just hue.
					tint = 'hsl(' + hue + ' 55% 89%)';
				}
			}

			// Invaded renders keep their original pixels (and the SVG filter)
			// but still get re-framed so every tile lines up uniformly.
			if (!invaded) sctx.putImageData(image, 0, 0);

			// Re-frame: fit the content box on a 3:5 canvas with an even margin
			const outW = OUT_W;
			const outH = Math.round(OUT_W / ASPECT);
			const m = Math.round(MARGIN * outW);
			const bw = maxX - minX + 1;
			const bh = maxY - minY + 1;
			const scale = Math.min((outW - 2 * m) / bw, (outH - 2 * m) / bh);
			const dw = bw * scale;
			const dh = bh * scale;
			const out = document.createElement('canvas');
			out.width = outW;
			out.height = outH;
			out.getContext('2d').drawImage(src, minX, minY, bw, bh, (outW - dw) / 2, (outH - dh) / 2, dw, dh);

			return new Promise((resolve) => {
				out.toBlob((blob) => resolve(blob ? { url: URL.createObjectURL(blob), tint, keepFilter: invaded } : { url: null, tint }), 'image/png');
			});
		};

		const processImage = (srcUrl) => {
			if (cache.has(srcUrl)) return cache.get(srcUrl);
			const promise = new Promise((resolve) => {
				let url;
				try {
					url = new URL(srcUrl, location.href);
				} catch (e) {
					return resolve(null);
				}
				url.searchParams.set('width', String(OUT_W));
				const probe = new Image();
				probe.crossOrigin = 'anonymous';
				probe.onload = () => {
					try {
						resolve(keyAndCrop(probe));
					} catch (e) {
						resolve(null);
					}
				};
				probe.onerror = () => resolve(null);
				probe.src = url.toString();
			});
			cache.set(srcUrl, promise);
			return promise;
		};

		const process = async (img) => {
			if (processed.has(img)) return;
			processed.add(img);
			const tile = img.closest('.pin-tile');
			if (!tile || tile.classList.contains('pin-tile--promo')) return;
			const result = await processImage(img.currentSrc || img.src);
			if (!result) return;
			if (result.url) {
				img.removeAttribute('srcset');
				img.removeAttribute('sizes');
				img.src = result.url;
				// Framed = margins baked into the canvas (drop the CSS padding).
				// keepFilter marks invaded (clear-case) renders: re-framed but
				// unmodified pixels, so the SVG white-key stays on.
				img.classList.add('pin-img--framed');
				if (!result.keepFilter) img.classList.add('pin-img--keyed');
			}
			// --neutral (editor switch): keep the keyed framing but skip the
			// per-design colour — every card stays on the default neutral.
			const neutral = tile.closest('.pinterest-grid--neutral');
			if (!neutral && result.tint) tile.style.setProperty('--pin-tint', result.tint);
		};

		// Lazy images (and the async recommendations) arrive over time
		document.addEventListener(
			'load',
			(event) => {
				const img = event.target;
				if (img instanceof HTMLImageElement && !img.classList.contains('pin-img--keyed') && img.closest && img.closest('.pinterest-grid--tint')) {
					process(img);
				}
			},
			true
		);
		document.querySelectorAll('.pinterest-grid--tint .pin-tile img').forEach((img) => {
			if (img.complete && img.naturalWidth) process(img);
		});
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initPinTints);
	} else {
		initPinTints();
	}

	/* --- Product page: per-design hero tint --------------------------------------
	   When the Product page section's "Tint background by design colour"
	   toggle is on (marker [data-bs-tint-hero]), sample the first gallery
	   render's dominant saturated hue on a tiny canvas and set --bs-hero-tint
	   on the section: the band behind the gallery + buy box takes a light
	   pastel of the design, and the white-key blend makes the photos sit on
	   it seamlessly. Result cached per product for the session, so repeat
	   views paint without re-sampling. B/w designs set nothing (neutral). */

	const initHeroTint = () => {
		const marker = document.querySelector('.shopify-section--main-product [data-bs-tint-hero]');
		if (!marker) return;
		const section = marker.closest('.shopify-section--main-product');
		if (!section) return;

		const applyTint = (value) => {
			// On <html>, so the body backdrop (behind the header pill) and
			// anything else reading the var can see it.
			document.documentElement.style.setProperty('--bs-hero-tint', value);
		};

		const cacheKey = 'bs-hero-tint:' + location.pathname;
		let cached = null;
		try {
			cached = sessionStorage.getItem(cacheKey);
		} catch (e) {
			/* storage unavailable — sample every view */
		}
		if (cached) {
			if (cached !== 'none') applyTint(cached);
			return;
		}

		const galleryImg = section.querySelector('.product-gallery__media img');
		if (!galleryImg) return;
		let url;
		try {
			url = new URL(galleryImg.currentSrc || galleryImg.src, location.href);
		} catch (e) {
			return;
		}
		// Same source width and same interior-only sampling as the Pinterest
		// grid, so a design's hero band and its grid tile always agree on the
		// hue (whole-image thumbnail sampling picked different winners on
		// near-tie designs).
		url.searchParams.set('width', '600');

		const probe = new Image();
		probe.crossOrigin = 'anonymous';
		probe.onload = () => {
			const w = probe.naturalWidth;
			const h = probe.naturalHeight;
			const canvas = document.createElement('canvas');
			canvas.width = w;
			canvas.height = h;
			const ctx = canvas.getContext('2d', { willReadFrequently: true });
			ctx.drawImage(probe, 0, 0);
			let data;
			try {
				data = ctx.getImageData(0, 0, w, h).data;
			} catch (e) {
				return;
			}
			const n = w * h;

			const passable = (i) => {
				const r = data[i * 4];
				const g = data[i * 4 + 1];
				const b = data[i * 4 + 2];
				return Math.max(r, g, b) - Math.min(r, g, b) <= 26 && r + g + b >= 585;
			};
			const outside = new Uint8Array(n);
			const stack = [];
			for (let x = 0; x < w; x++) {
				if (passable(x)) stack.push(x);
				const bot = (h - 1) * w + x;
				if (passable(bot)) stack.push(bot);
			}
			for (let y = 0; y < h; y++) {
				const left = y * w;
				if (passable(left)) stack.push(left);
				const right = y * w + w - 1;
				if (passable(right)) stack.push(right);
			}
			while (stack.length) {
				const i = stack.pop();
				if (outside[i]) continue;
				outside[i] = 1;
				const x = i % w;
				if (x > 0 && !outside[i - 1] && passable(i - 1)) stack.push(i - 1);
				if (x < w - 1 && !outside[i + 1] && passable(i + 1)) stack.push(i + 1);
				if (i >= w && !outside[i - w] && passable(i - w)) stack.push(i - w);
				if (i < n - w && !outside[i + w] && passable(i + w)) stack.push(i + w);
			}

			const buckets = Array.from({ length: 12 }, () => ({ w: 0, x: 0, y: 0 }));
			let saturated = 0;
			for (let i = 0; i < n; i++) {
				if (outside[i]) continue;
				const p = i * 4;
				const r = data[p] / 255;
				const g = data[p + 1] / 255;
				const b = data[p + 2] / 255;
				const mx = Math.max(r, g, b);
				const mn = Math.min(r, g, b);
				const l = (mx + mn) / 2;
				const dd = mx - mn;
				if (dd < 0.08 || l < 0.15 || l > 0.92) continue;
				const s = dd / (1 - Math.abs(2 * l - 1));
				if (s < 0.2) continue;
				let hue;
				if (mx === r) hue = ((g - b) / dd + 6) % 6;
				else if (mx === g) hue = (b - r) / dd + 2;
				else hue = (r - g) / dd + 4;
				hue *= 60;
				saturated++;
				const bucket = buckets[Math.floor(hue / 30) % 12];
				bucket.w += s;
				bucket.x += Math.cos((hue * Math.PI) / 180) * s;
				bucket.y += Math.sin((hue * Math.PI) / 180) * s;
			}
			let tint = null;
			// Same 5% bar as the grid so band and tile always agree.
			if (saturated / n >= 0.05) {
				const best = buckets.reduce((a, b) => (b.w > a.w ? b : a));
				if (best.w) {
					const hue = Math.round(((Math.atan2(best.y, best.x) * 180) / Math.PI + 360) % 360);
					// Lighter than the grid tiles: this is a large surface and
					// the white buy-box card needs to stay clearly on top.
					tint = 'hsl(' + hue + ' 50% 92%)';
				}
			}
			try {
				sessionStorage.setItem(cacheKey, tint || 'none');
			} catch (e) {
				/* ignore */
			}
			if (tint) applyTint(tint);
		};
		probe.src = url.toString();
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initHeroTint);
	} else {
		initHeroTint();
	}

	/* --- Product gallery: flood-keyed images for the tinted hero ----------------
	   On a tinted band the white-key filter's residual compression halo
	   around the phone becomes visible, so when the hero tint is active the
	   gallery images get the same geometric treatment as the grid — flood
	   fill from the borders keys the true background and converts shadows to
	   soft alpha — but with NO reframing, so angled/lifestyle shots keep
	   their original composition (photos without a white background are left
	   untouched by construction). The SVG filter covers the gap until each
	   image is processed. */

	const initGalleryKey = () => {
		if (!document.querySelector('.shopify-section--main-product [data-bs-tint-hero]')) return;
		// White-cards gallery style: renders stay untouched in white boxes —
		// no flood keying (and CSS disables the filter).
		if (document.querySelector('.shopify-section--main-product [data-bs-gallery-cards]')) return;

		const MAX_W = 1000;
		const processed = new WeakSet();
		const cache = new Map();

		const keyInPlace = (probe) => {
			const w = probe.naturalWidth;
			const h = probe.naturalHeight;
			const canvas = document.createElement('canvas');
			canvas.width = w;
			canvas.height = h;
			const ctx = canvas.getContext('2d', { willReadFrequently: true });
			ctx.drawImage(probe, 0, 0);
			let image;
			try {
				image = ctx.getImageData(0, 0, w, h);
			} catch (e) {
				return null;
			}
			const d = image.data;
			const n = w * h;

			const passable = (i) => {
				const r = d[i * 4];
				const g = d[i * 4 + 1];
				const b = d[i * 4 + 2];
				return Math.max(r, g, b) - Math.min(r, g, b) <= 26 && r + g + b >= 585;
			};

			const outside = new Uint8Array(n);
			const stack = [];
			for (let x = 0; x < w; x++) {
				if (passable(x)) stack.push(x);
				const bot = (h - 1) * w + x;
				if (passable(bot)) stack.push(bot);
			}
			for (let y = 0; y < h; y++) {
				const left = y * w;
				if (passable(left)) stack.push(left);
				const right = y * w + w - 1;
				if (passable(right)) stack.push(right);
			}
			if (!stack.length) return null; // no white border — lifestyle shot, leave as-is
			let outsideCount = 0;
			while (stack.length) {
				const i = stack.pop();
				if (outside[i]) continue;
				outside[i] = 1;
				outsideCount++;
				const x = i % w;
				if (x > 0 && !outside[i - 1] && passable(i - 1)) stack.push(i - 1);
				if (x < w - 1 && !outside[i + 1] && passable(i + 1)) stack.push(i + 1);
				if (i >= w && !outside[i - w] && passable(i - w)) stack.push(i - w);
				if (i < n - w && !outside[i + w] && passable(i + w)) stack.push(i + w);
			}
			if (outsideCount / n < 0.05) return null; // barely any background — not a studio render

			// Invasion check (clear cases): if the flood reached the image
			// centre it leaked through a light bumper into the phone interior;
			// keying would look blotchy, so keep the SVG filter instead.
			const cx0 = Math.floor(w * 0.35);
			const cx1 = Math.ceil(w * 0.65);
			const cy0 = Math.floor(h * 0.35);
			const cy1 = Math.ceil(h * 0.65);
			let coreOutside = 0;
			for (let y = cy0; y < cy1; y++) {
				for (let x = cx0; x < cx1; x++) {
					if (outside[y * w + x]) coreOutside++;
				}
			}
			if (coreOutside / ((cx1 - cx0) * (cy1 - cy0)) > 0.05) return null;

			for (let i = 0; i < n; i++) {
				if (!outside[i]) continue;
				const p = i * 4;
				const lum = (d[p] + d[p + 1] + d[p + 2]) / 3;
				d[p] = 0;
				d[p + 1] = 0;
				d[p + 2] = 0;
				d[p + 3] = Math.max(0, Math.round(255 - lum));
			}
			ctx.putImageData(image, 0, 0);
			return new Promise((resolve) => {
				canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : null), 'image/png');
			});
		};

		const processImage = (srcUrl) => {
			if (cache.has(srcUrl)) return cache.get(srcUrl);
			const promise = new Promise((resolve) => {
				let url;
				try {
					url = new URL(srcUrl, location.href);
				} catch (e) {
					return resolve(null);
				}
				url.searchParams.set('width', String(MAX_W));
				const probe = new Image();
				probe.crossOrigin = 'anonymous';
				probe.onload = () => {
					try {
						const result = keyInPlace(probe);
						result && result.then ? result.then(resolve) : resolve(result);
					} catch (e) {
						resolve(null);
					}
				};
				probe.onerror = () => resolve(null);
				probe.src = url.toString();
			});
			cache.set(srcUrl, promise);
			return promise;
		};

		const process = async (img) => {
			if (processed.has(img)) return;
			processed.add(img);
			const blobUrl = await processImage(img.currentSrc || img.src);
			if (!blobUrl) return;
			img.removeAttribute('srcset');
			img.removeAttribute('sizes');
			img.src = blobUrl;
			img.classList.add('bs-keyed');
		};

		document.addEventListener(
			'load',
			(event) => {
				const img = event.target;
				if (
					img instanceof HTMLImageElement &&
					!img.classList.contains('bs-keyed') &&
					img.closest &&
					img.closest('.shopify-section--main-product .product-gallery__media')
				) {
					process(img);
				}
			},
			true
		);
		document.querySelectorAll('.shopify-section--main-product .product-gallery__media img').forEach((img) => {
			if (img.complete && img.naturalWidth) process(img);
		});
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initGalleryKey);
	} else {
		initGalleryKey();
	}

	/* --- Desktop gallery: safety cap at 15 visible images -----------------------
	   The desktop grid shows every image of the selected variant's group. It
	   used to cap at 5 with a "View all N photos" pill; since 2026-10-08 the
	   whole group shows by default (Scott: the marketing tail and customer
	   media are the point, nobody should have to click for them) and the pill
	   only appears past 15 - a guard for a broken product whose variant
	   grouping fails and would otherwise dump 180 images onto the page.
	   Visibility is per variant group (the theme hides off-group media with
	   the hidden attribute), so the cap recomputes on every variant change.
	   CSS scopes the hiding to >=1000px, so the mobile carousel always has
	   the full set. */

	const initGalleryCap = () => {
		const gallery = document.querySelector('.shopify-section--main-product .product-gallery--desktop-grid');
		if (!gallery) return;
		const list = gallery.querySelector('.product-gallery__media-list');
		if (!list) return;
		const MAX = 15;

		let button = null;
		const ensureButton = () => {
			if (button) return button;
			button = document.createElement('button');
			button.type = 'button';
			button.className = 'bs-gallery-more';
			button.addEventListener('click', () => {
				gallery.classList.add('bs-show-all');
				apply();
			});
			list.appendChild(button);
			return button;
		};

		const apply = () => {
			const media = Array.from(list.querySelectorAll('.product-gallery__media'));
			const visible = media.filter((m) => !m.hidden);
			media.forEach((m) => m.classList.remove('bs-gallery-extra'));
			const showAll = gallery.classList.contains('bs-show-all');
			if (!showAll) {
				visible.forEach((m, i) => {
					if (i >= MAX) m.classList.add('bs-gallery-extra');
				});
			}
			const extra = visible.length - MAX;
			const btn = ensureButton();
			if (extra > 0 && !showAll) {
				btn.textContent = 'View all ' + visible.length + ' photos';
				btn.hidden = false;
			} else {
				btn.hidden = true;
			}
		};

		document.addEventListener('variant:change', () => setTimeout(apply, 50));
		apply();
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initGalleryCap);
	} else {
		initGalleryCap();
	}

	/* --- Mobile gallery: one-time swipe nudge ------------------------------------
	   A gentle sideways bump on first view teaches that the gallery swipes
	   (full-width card slides hide the next-image peek). Slides shift ~44px
	   and settle back; runs once per session, skips if the user has already
	   swiped, and respects prefers-reduced-motion. Deliberately NOT an
	   autoplaying carousel: customers are inspecting the design — motion
	   they didn't ask for on a PDP costs more than it reveals. */

	const initGalleryNudge = () => {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
		if (!window.matchMedia('screen and (max-width: 999px)').matches) return;
		const list = document.querySelector('.shopify-section--main-product .product-gallery__media-list');
		if (!list) return;
		if (list.querySelectorAll('.product-gallery__media:not([hidden])').length < 2) return;
		try {
			if (sessionStorage.getItem('bs-gallery-nudged')) return;
		} catch (e) {
			/* no storage — nudge anyway */
		}

		setTimeout(() => {
			if (list.scrollLeft > 4) return; // already exploring
			try {
				sessionStorage.setItem('bs-gallery-nudged', '1');
			} catch (e) {
				/* ignore */
			}
			list.querySelectorAll(':scope > *:not([hidden])').forEach((slide) => {
				slide.animate(
					[
						{ transform: 'translateX(0)' },
						{ transform: 'translateX(-44px)', offset: 0.4 },
						{ transform: 'translateX(0)' },
					],
					{ duration: 900, easing: 'cubic-bezier(0.4, 0, 0.22, 1)' }
				);
			});
		}, 1400);
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initGalleryNudge);
	} else {
		initGalleryNudge();
	}

	/* --- Floating dots: keep the current dot in the 4-dot window ---------------
	   The dots pill clips to ~4 dots (CSS); as the carousel moves, scroll
	   the active dot to the window centre so progress reads iOS-style. */

	const initDotsWindow = () => {
		// both dot styles since 2026-10-08: the plain dots window to 6 as well (custom.css) now that
		// the marketing tail + customer media can push a gallery past ten slides (Scott)
		const dots = document.querySelector('.shopify-section--main-product .product-gallery page-dots.page-dots');
		if (!dots) return;
		const center = () => {
			const current = dots.querySelector('[aria-current="true"]');
			if (!current) return;
			// rects, not offsetLeft: the plain dots are not a positioned box, so offsetLeft
			// measured from the gallery and the first dot sat half out of the window
			const box = dots.getBoundingClientRect();
			const rect = current.getBoundingClientRect();
			const target = dots.scrollLeft + (rect.left - box.left) - box.width / 2 + rect.width / 2;
			dots.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
		};
		new MutationObserver(center).observe(dots, { attributes: true, attributeFilter: ['aria-current'], subtree: true });
		center();
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initDotsWindow);
	} else {
		initDotsWindow();
	}

	/* --- Product details column: adopt supporting sections ----------------------
	   Moves configured video feedback, then product accordions, into product-info
	   so the right side is one continuous flow. Keeping them inside product-rerender
	   lets the existing safe-sticky behaviour scroll a tall column naturally and
	   hold its bottom edge until the gallery ends. The observer re-adopts the same
	   section nodes if a full product rerender replaces the product-info element. */

	const initStickyLayout = () => {
		const layout = document.querySelector('.shopify-section--main-product [data-bs-product-layout]');
		if (!layout) return;

		let accordions = document.querySelector('.shopify-section--product-accordions');
		let videoFeedback = document.querySelector('.shopify-section--video-feedback video-feedback')?.closest('.shopify-section');

		const adoptSections = () => {
			const productInfo = layout.querySelector('.product-info');
			if (!productInfo) return;

			videoFeedback = document.querySelector('.shopify-section--video-feedback video-feedback')?.closest('.shopify-section') || videoFeedback;
			accordions = document.querySelector('.shopify-section--product-accordions') || accordions;

			if (videoFeedback && videoFeedback.parentElement !== productInfo) {
				productInfo.appendChild(videoFeedback);
				layout.setAttribute('data-bs-has-video-feedback', '');
			}

			if (accordions && accordions.parentElement !== productInfo) {
				productInfo.appendChild(accordions);
			}
		};

		adoptSections();

		if (videoFeedback || accordions) {
			new MutationObserver(adoptSections).observe(layout, { childList: true, subtree: true });
		}
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initStickyLayout);
	} else {
		initStickyLayout();
	}
})();

/* --- BLANC SPACE CUSTOM: support chat launcher links -----------------------
   The chat widget (Chatwoot, snippets/support-chat.liquid) is deliberately
   absent from a visitor's first page. A support link (#chat, or the legacy
   #intercom spelling in older content) asks the delayed loader to fetch it
   immediately; without a loader, normal navigation remains the safe fallback. */
document.addEventListener('click', (event) => {
	const link = event.target.closest?.('a[href*="#intercom"], a[href*="#chat"]');
	if (!link) return;
	let hash = '';
	try { hash = new URL(link.href, window.location.href).hash; } catch (error) { return; }
	if (hash !== '#intercom' && hash !== '#chat') return;
	const chat = window.BlancSpaceSupportChat;
	if (!chat?.open) return;
	event.preventDefault();
	chat.open(link.href);
});

/* --- BLANC SPACE CUSTOM: PDP rating link prefers the theme-native Loox API
   section, then falls back to the app widget while the API display is tested. */
document.addEventListener('click', (event) => {
	const link = event.target.closest?.('[data-scroll-to-reviews]');
	if (!link) return;
	const target = document.querySelector('#loox-api-reviews') || document.querySelector('[id$="__loox-reviews"]');
	if (!target) return;
	event.preventDefault();
	target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

/* --- BLANC SPACE CUSTOM: Shipping Protection (appless) ---------------------
   Replaces the $40/mo shipping-protection app. The protection product
   (14963366527349 / variant 55527658226037) is a real cart line so checkout
   charges it, but the drawer renders it only as the pre-selected footer
   switch (snippets/cart-shipping-protection.liquid) and hides its line-item
   card. This block owns the cart-state rules:
   - auto-add whenever real items exist, unless the visitor switched it off
     (persisted in localStorage so their choice is never overridden);
   - pin its quantity to 1;
   - never leave it as the only line — a protection-only cart is emptied, and
     that intermediate cart:change is swallowed so the drawer goes straight
     to its empty state;
   - the header count bubble excludes it: the Liquid renders in the header
     sections subtract it and the CartCount setter patch below keeps live
     updates consistent via window.__bsProtectionQty. */
(() => {
	const PROTECTION_PRODUCT_ID = 14963366527349;
	const PROTECTION_VARIANT_ID = 55527658226037;
	const OPTOUT_KEY = 'blancspace.shipping-protection';

	const optedOut = () => {
		try { return localStorage.getItem(OPTOUT_KEY) === 'off'; } catch (error) { return false; }
	};
	const setOptedOut = (off) => {
		try { off ? localStorage.setItem(OPTOUT_KEY, 'off') : localStorage.removeItem(OPTOUT_KEY); } catch (error) { /* storage unavailable */ }
	};

	const protectionLine = (cart) => (cart?.items || []).find((item) => item.product_id === PROTECTION_PRODUCT_ID);

	window.__bsProtectionQty = 0;
	// Exposed for the quantity patch, which bundles the protection removal into
	// the same request when the last real item is removed.
	window.__bsProtectionVariantId = PROTECTION_VARIANT_ID;
	let busy = false;
	let addFailed = false; // a failing auto-add must not retry on every cart event

	/* cart/update.js both adds (variant absent) and removes (quantity 0), and
	   returns the full cart, so every mutation shares one call. The bundled
	   sections + cart:change dispatch mirror the vendor's LineItemQuantity
	   contract so the drawer, count bubble and offer engine all refresh. */
	const setProtectionQuantity = async (quantity) => {
		const sections = [];
		document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
		const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ updates: { [PROTECTION_VARIANT_ID]: quantity }, sections }),
		});
		if (!response.ok) throw new Error(`Shipping protection update failed (${response.status})`);
		const cart = await response.json();
		if (window.themeVariables?.settings?.pageType === 'cart') {
			// /cart renders its totals server-side; mirror the vendor's reload-on-change.
			window.location.reload();
			return;
		}
		document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
			bubbles: true,
			detail: { baseEvent: 'line-item:change', cart },
		}));
	};

	/* Bring the cart in line with the rules above. Returns true when the
	   triggering render should be suppressed (protection-only cart on its way
	   to empty — the follow-up dispatch carries the emptied cart). */
	const reconcile = (cart) => {
		const line = protectionLine(cart);
		window.__bsProtectionQty = line ? line.quantity : 0;
		if (busy) return false;

		let target = null;
		if (line && cart.items.length === 1) target = 0;
		else if (line && line.quantity !== 1) target = 1;
		else if (!line && cart.items.length > 0 && !optedOut() && !addFailed) target = 1;
		if (target === null) return false;

		busy = true;
		setProtectionQuantity(target)
			.catch(() => { if (target === 1 && !line) addFailed = true; })
			.finally(() => { busy = false; });
		return target === 0;
	};

	/* Capture phase: __bsProtectionQty must be current before the vendor's
	   bubble listeners (CartCount, CartDrawer) consume the same event. */
	document.addEventListener('cart:change', (event) => {
		const cart = event.detail?.cart;
		if (!cart?.items) return;
		if (reconcile(cart)) event.stopPropagation();
	}, true);

	/* Page loads, BF-cache restores and cart:refresh don't produce a
	   cart:change with items, so reconcile from the server state directly.
	   Re-setting itemCount routes the raw count through the patched setter,
	   correcting any bubble the vendor updated before __bsProtectionQty. */
	const reconcileFromServer = async () => {
		try {
			window.bsMark?.('cart-reconcile');
			const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
			window.bsMark?.('cart-reconcile-apply');
			reconcile(cart);
			document.querySelectorAll('cart-count').forEach((element) => { element.itemCount = cart.item_count; });
			// Failsafe for a stale write that raced ahead of the fetch above:
			// the animated setter can skip its final text swap when its motion
			// promise rejects, so assert the resolved value directly once the
			// animation window has passed.
			const line = protectionLine(cart);
			const expected = Math.max(0, cart.item_count - (line ? line.quantity : 0));
			setTimeout(() => {
				document.querySelectorAll('cart-count').forEach((element) => {
					if (parseInt(element.textContent) !== expected) element.textContent = expected;
				});
			}, 800);
		} catch (error) { /* keep the Liquid-rendered state */ }
	};
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => reconcileFromServer());
	} else {
		reconcileFromServer();
	}
	document.addEventListener('cart:refresh', () => reconcileFromServer());
	window.addEventListener('pageshow', (event) => { if (event.persisted) reconcileFromServer(); });

	/* The footer switch. On success the bundled-section re-render replaces the
	   whole row with the fresh state; on failure the switch snaps back. */
	document.addEventListener('change', (event) => {
		const toggle = event.target.closest?.('[data-bs-protection-toggle]');
		if (!toggle) return;
		const wantsProtection = toggle.checked;
		if (busy) {
			toggle.checked = !wantsProtection;
			return;
		}
		setOptedOut(!wantsProtection);
		addFailed = false;
		busy = true;
		toggle.disabled = true;
		window.bsTrack?.('shipping_protection_toggled', { enabled: wantsProtection });
		setProtectionQuantity(wantsProtection ? 1 : 0)
			.catch(() => {
				toggle.checked = !wantsProtection;
				toggle.disabled = false;
			})
			.finally(() => { busy = false; });
	});

	/* Vendor CartCount writes raw item_count into the bubble; subtract the
	   protection quantity so the badge only counts what the customer chose.
	   Its server-driven refresh (_updateFromServer, fired on every pageshow
	   and cart:refresh) is also replaced: it used to race our bookkeeping —
	   on a slow page its stale write landed after ours and the bubble briefly
	   counted the protection line — so the patched version derives the
	   protection quantity from the same cart payload it renders. */
	customElements.whenDefined('cart-count').then(() => {
		const proto = customElements.get('cart-count').prototype;
		const descriptor = Object.getOwnPropertyDescriptor(proto, 'itemCount');
		if (!descriptor?.set) return;
		Object.defineProperty(proto, 'itemCount', {
			configurable: true,
			get: descriptor.get,
			set(count) { descriptor.set.call(this, Math.max(0, count - (window.__bsProtectionQty || 0))); },
		});

		proto._updateFromServer = async function () {
			try {
				const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
				const line = protectionLine(cart);
				window.__bsProtectionQty = line ? line.quantity : 0;
				this.itemCount = cart.item_count;
			} catch (error) { /* keep the current bubble */ }
		};
	});
})();

/* --- BLANC SPACE CUSTOM: express checkout routing --------------------------
   Apple Pay / Shop Pay next to Secure Checkout, 50/50, on all three cart
   surfaces (drawer footer, /cart recap, added-to-cart sheet's even-pair
   state). content_for_additional_checkout_buttons renders exactly once, in
   the hidden #bs-express-holder (snippets/express-checkout-holder.liquid),
   because Shopify hydrates those buttons once per page load — any surface
   re-render would kill them. This block keeps a live reference to the wallet
   node (surface re-renders detach it; the reference lets us re-adopt it) and
   moves it into whichever [data-bs-express-slot] is active:
   - the /cart recap on cart-page loads;
   - the drawer's footer slot while the drawer is open (re-seated after every
     footer re-render);
   - the added-to-cart sheet's slot while it is open.
   A slot only unhides once an allowed wallet button actually exists in the
   wallet node (hydration is async — a MutationObserver re-checks), so
   ineligible devices keep the full-width checkout button. */
(() => {
	const walletNode = document.querySelector('[data-bs-express-wallets]');
	if (!walletNode) return;
	const holder = document.getElementById('bs-express-holder');

	/* Shopify renders <shopify-accelerated-checkout-cart wallet-configs="[…]">.
	   Filtering the config JSON before the component hydrates is the only
	   reliable lever: its closed-shadow layout builds one cell per configured
	   wallet and behaves unpredictably with two. So exactly ONE wallet is
	   configured — Apple Pay on Apple-Pay-capable devices (top method in our
	   payment data), Shop Pay everywhere else (Google Pay was 1.4% of orders;
	   every wallet remains available inside checkout itself). */
	const ALLOWED_WALLETS = (() => {
		try {
			if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) return ['apple_pay'];
		} catch (error) { /* non-Apple browsers throw or lack the API */ }
		return ['shop_pay'];
	})();
	const component = walletNode.querySelector('shopify-accelerated-checkout-cart');
	if (component) {
		try {
			const configs = JSON.parse(component.getAttribute('wallet-configs') || '[]');
			const allowed = configs.filter((config) => ALLOWED_WALLETS.includes(config.name));
			if (allowed.length !== configs.length) component.setAttribute('wallet-configs', JSON.stringify(allowed));
			if (!allowed.length) component.remove();
		} catch (error) { component.remove(); }
	}

	/* The component may render inside shadow DOM, so eligibility is judged by
	   rendered height (only measurable while the slot is unhidden — place()
	   handles that) with the legacy class-based markup as a fallback. */
	const ELIGIBLE = '[class*="apple-pay"], [class*="apple_pay"], [class*="shop-pay"], [class*="shop_pay"]';
	const hasEligibleWallet = () => {
		const liveComponent = walletNode.querySelector('shopify-accelerated-checkout-cart');
		if (liveComponent) return liveComponent.offsetHeight > 8;
		return !!walletNode.querySelector(ELIGIBLE);
	};
	const onCartPage = () => window.themeVariables?.settings?.pageType === 'cart';

	let activeSlotName = null;

	const park = () => {
		activeSlotName = null;
		document.querySelectorAll('[data-bs-express-slot]').forEach((slot) => { slot.hidden = true; });
		if (holder && walletNode.parentElement !== holder) holder.appendChild(walletNode);
	};

	/* Wallet hydration is async and height can only be measured unhidden, so
	   a placement that comes up empty is re-tried for a few seconds. The
	   unhide → measure → re-hide sequence is synchronous (no paint between). */
	let retryTimer = null;
	let retriesLeft = 0;
	const place = (name, isRetry) => {
		window.bsMark?.(isRetry ? 'express-place-retry' : 'express-place');
		const slot = document.querySelector(`[data-bs-express-slot="${name}"]`);
		if (!slot) return park();
		activeSlotName = name;
		if (!isRetry) retriesLeft = 8;
		if (walletNode.parentElement !== slot) slot.appendChild(walletNode);
		slot.hidden = false;
		if (!hasEligibleWallet()) slot.hidden = true;
		clearTimeout(retryTimer);
		if (slot.hidden && retriesLeft > 0) {
			retriesLeft -= 1;
			retryTimer = setTimeout(() => { if (activeSlotName === name) place(name, true); }, 700);
		}
	};

	const reseat = () => {
		if (activeSlotName) place(activeSlotName);
		else if (onCartPage()) place('page');
	};

	// Wallet hydration is async and can arrive after the first placement.
	new MutationObserver(reseat).observe(walletNode, { childList: true, subtree: true });

	// Which surface is active. Capture phase so this runs before any vendor
	// bubble listeners re-render the surface the wallet is being seated into.
	document.addEventListener('dialog:after-show', (event) => {
		const target = event.target;
		if (target?.id === 'cart-drawer') place('drawer');
		else if (target?.tagName === 'CART-NOTIFICATION-DRAWER') place('added');
	}, true);

	document.addEventListener('dialog:after-hide', (event) => {
		if (event.target?.id !== 'cart-drawer' && event.target?.tagName !== 'CART-NOTIFICATION-DRAWER') return;
		if (onCartPage()) place('page');
		else park();
	}, true);

	// The drawer footer (and its slot) is rebuilt on every cart change; the
	// added-to-cart sheet rebuilds per add. Re-seat after those listeners ran.
	document.addEventListener('cart:change', () => { setTimeout(reseat, 0); });
	document.addEventListener('variant:add', () => { setTimeout(reseat, 0); });
	// A failed add (and discount application) rebuilds the whole drawer via
	// cart:refresh, detaching the slot that holds the wallet node.
	document.addEventListener('cart:refresh', () => { setTimeout(reseat, 0); });

	if (onCartPage()) {
		if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => place('page'));
		else place('page');
	}
})();

/* --- BLANC SPACE CUSTOM: Shipping Protection info tooltip -------------------
   The footer row carries no explainer text; the "i" button beside the price
   toggles a tooltip bubble instead. Outside taps and Escape dismiss it. The
   button lives outside the switch's label so it never flips the checkbox. */
(() => {
	const closeAll = (except) => {
		document.querySelectorAll('[data-bs-shipping-protection]').forEach((row) => {
			if (row === except) return;
			const tooltip = row.querySelector('.bs-shipping-protection__tooltip');
			if (tooltip && !tooltip.hidden) {
				tooltip.hidden = true;
				row.querySelector('[data-bs-protection-info]')?.setAttribute('aria-expanded', 'false');
			}
		});
	};

	document.addEventListener('click', (event) => {
		const info = event.target.closest?.('[data-bs-protection-info]');
		if (!info) {
			closeAll(null);
			return;
		}
		const row = info.closest('[data-bs-shipping-protection]');
		const tooltip = row?.querySelector('.bs-shipping-protection__tooltip');
		if (!tooltip) return;
		closeAll(row);
		tooltip.hidden = !tooltip.hidden;
		info.setAttribute('aria-expanded', String(!tooltip.hidden));
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') closeAll(null);
	});
})();

/* --- Gallery advisory pill -----------------------------------------------------
   snippets/product-note.liquid: dismisses via its X or fades itself out
   after 5 seconds. Removal (not just hiding) keeps the gallery overlay
   stack clean for the zoom control and badge row. */
(() => {
	class BsProductNote extends HTMLElement {
		connectedCallback() {
			this._dismiss = this._dismiss.bind(this);
			this._timer = setTimeout(this._dismiss, 5000);
			this.querySelector('[data-note-close]')?.addEventListener('click', this._dismiss);
		}

		disconnectedCallback() {
			clearTimeout(this._timer);
		}

		_dismiss() {
			clearTimeout(this._timer);
			this.classList.add('is-dismissed');
			// Matches the 250ms CSS fade; reduced-motion just removes a bit early.
			setTimeout(() => this.remove(), 300);
		}
	}

	if (!customElements.get('bs-product-note')) {
		customElements.define('bs-product-note', BsProductNote);
	}
})();

/* --- BLANC SPACE CUSTOM: cart thumbnail zoom --------------------------------
   Tapping a cart line-item image opens a larger preview — user testing showed
   shoppers wanting to double-check their personalised Customily previews.
   Deliberately a plain fixed <div>, NOT dialog.showModal(): top-layer
   promotion of dialog subtrees has crashed iOS WebKit before (AGENTS.md). */
(() => {
	let overlay = null;
	let lastTrigger = null;

	const close = () => {
		if (!overlay) return;
		const node = overlay;
		overlay = null;
		node.removeAttribute('data-open');
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) node.remove();
		else setTimeout(() => node.remove(), 220);
		lastTrigger?.focus?.({ preventScroll: true });
		lastTrigger = null;
	};

	const open = (trigger) => {
		close();
		const src = trigger.getAttribute('data-bs-zoom-src');
		if (!src) return;
		lastTrigger = trigger;
		overlay = document.createElement('div');
		overlay.className = 'bs-image-zoom';
		overlay.setAttribute('role', 'dialog');
		overlay.setAttribute('aria-modal', 'true');
		overlay.setAttribute('aria-label', 'Larger image preview');
		const frame = document.createElement('div');
		frame.className = 'bs-image-zoom__frame';
		const image = document.createElement('img');
		image.alt = trigger.getAttribute('aria-label') || '';
		image.src = src;
		const closeButton = document.createElement('button');
		closeButton.type = 'button';
		closeButton.className = 'bs-image-zoom__close';
		closeButton.setAttribute('aria-label', 'Close image preview');
		closeButton.textContent = '×';
		frame.append(image, closeButton);
		overlay.append(frame);
		// Mount INSIDE the drawer when opened from one: the vendor focus trap
		// hides the drawer on any touch that ends outside it (_allowOutsideClick
		// in theme.js), so a body-mounted overlay closed the cart underneath on
		// every tap. position:fixed still spans the viewport — no ancestor here
		// creates a containing block.
		const host = trigger.closest('cart-drawer, wishlist-drawer, cart-notification-drawer') || document.body;
		host.append(overlay);
		// The desktop added-to-cart popover clips its slotted children to its own box, so a
		// viewport-sized overlay showed only grey (the image sat centred on the page, outside
		// it). There, fit the overlay to the popover instead. Phones get the full-screen sheet.
		const hostBox = host.tagName === 'CART-NOTIFICATION-DRAWER' ? host.getBoundingClientRect() : null;
		if (hostBox && hostBox.width < window.innerWidth * 0.7) {
			Object.assign(overlay.style, { top: `${hostBox.top}px`, left: `${hostBox.left}px`, width: `${hostBox.width}px`, height: `${hostBox.height}px`, right: 'auto', bottom: 'auto', padding: 'var(--spacing-4)', borderRadius: 'var(--rounded-lg)' });
			overlay.style.setProperty('--bs-zoom-max-w', `${Math.round(hostBox.width - 48)}px`);
			overlay.style.setProperty('--bs-zoom-max-h', `${Math.round(hostBox.height - 140)}px`);
		}
		requestAnimationFrame(() => overlay?.setAttribute('data-open', ''));
		closeButton.focus({ preventScroll: true });
	};

	document.addEventListener('click', (event) => {
		const trigger = event.target.closest?.('[data-bs-cart-zoom]');
		if (trigger) {
			event.preventDefault();
			open(trigger);
			return;
		}
		if (!overlay) return;
		// Full-viewport surface: any tap outside the white frame (or on the X) closes.
		if (event.target.closest?.('.bs-image-zoom__close') || !event.target.closest?.('.bs-image-zoom__frame')) close();
	});

	// Capture phase: vendor drawers listen for Escape on bubble, so this must
	// run first or one Escape closes both the preview AND the cart drawer.
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && overlay) {
			event.stopImmediatePropagation();
			event.preventDefault();
			close();
		}
	}, true);

	// Cart re-renders replace the line items; never leave a stale preview up.
	document.addEventListener('cart:change', close);
})();

/* --- PostHog commerce funnel events -------------------------------------------
   add_to_cart mirrors the theme's variant:add event (fired after every
   successful /cart/add.js, including upsells), so PDP, quick-add, popup and
   cart-upsell additions are all covered by one listener. checkout_started
   fires on any checkout control: the Secure Checkout submits (drawer, cart
   page, just-added popup) and the express Apple Pay / Shop Pay wallet node.
   Everything is guarded — analytics must never break shopping. */
(() => {
	const capture = (name, properties) => {
		try {
			window.posthog?.capture?.(name, properties);
		} catch (error) {
			/* never let analytics interfere with the purchase */
		}
	};

	const phoneColourProps = (item) => {
		const shown = window.bsPhoneColourShown;
		if (!shown || !shown.colour || String(shown.variantId) !== String(item.variant_id)) {
			return { phone_colour_shown: null };
		}
		return {
			phone_colour_shown: shown.colour,
			phone_colour_default: shown.dflt || null,
			phone_colour_is_default: shown.colour === shown.dflt,
			phone_colour_offered: shown.offered > 1,
		};
	};

	document.addEventListener('variant:add', (event) => {
		const detail = event.detail || {};
		const cart = detail.cart || {};
		(detail.items || []).forEach((item) => {
			if (!item || typeof item !== 'object') return;
			capture('add_to_cart', {
				// The pre-selected Shipping Protection line is added by the theme, not
				// the shopper; flag it so funnels can exclude it (PostHog `add_to_cart`
				// per session was inflated by it — 2026-09-07 audit).
				is_shipping_protection: item.variant_id === window.__bsProtectionVariantId || /shipping protection/i.test(item.product_title || ''),
				// a personalised line: the in-house Personaliser (_design_id) or Customily
				is_personalised: !!(item.properties && (item.properties._design_id || item.properties['_customily-personalization-id'])),
				product_id: item.product_id,
				variant_id: item.variant_id,
				product_title: item.product_title,
				variant_title: item.variant_title || null,
				handle: item.handle,
				// where the add came from when the theme knows (pdp_addon, pdp_addon_exact, pdp_addon_close); null = the main form or elsewhere
				add_source: item.bs_source || null,
				price: typeof item.price === 'number' ? item.price / 100 : null,
				quantity: item.quantity,
				currency: cart.currency || null,
				cart_total: typeof cart.total_price === 'number' ? cart.total_price / 100 : null,
				cart_item_count: cart.item_count ?? null,
				// phone colour the gallery showed for THIS variant (phone-colour-gallery.js); null when the
				// product has no engine media, or the model offers one finish only
				...phoneColourProps(item),
			});
		});
	});

	document.addEventListener(
		'click',
		(event) => {
			const target = event.target instanceof Element ? event.target : null;
			const control = target?.closest('button[name="checkout"], [data-bs-express-wallets]');
			if (!control) return;
			const isWallet = control.matches('[data-bs-express-wallets]');
			capture('checkout_started', {
				source: isWallet ? 'express_wallet' : 'checkout_button',
				surface: control.closest('#cart-drawer, cart-drawer') ? 'cart_drawer'
					: control.closest('.shopify-section--variant-added, [id*="variant-added"]') ? 'added_popup'
					: control.closest('.shopify-section--main-cart, [id*="main-cart"]') ? 'cart_page'
					: 'other',
			});
		},
		{ capture: true }
	);

	/* --- Phone-model dynamic content -------------------------------------------
	   PDP copy tokens ([model] → <span data-model-text>) and model-aware media
	   slots ([data-bs-model-alts] holding [data-bs-model-alt][data-model-match]
	   alternates, empty match = default) follow the selected Phone Model
	   (option1 by the storefront data contract; see AGENTS.md — Phone model
	   content). Only the main product form's variant:change may drive the page:
	   upsell/quick-buy forms elsewhere carry their own variants. Alternate
	   videos stay lazy — the promo-video observer initialises them once
	   revealed; already-initialised ones just resume. */
	const applyModelContent = (model) => {
		const name = typeof model === 'string' ? model.trim() : '';
		if (!name) return;

		document.querySelectorAll('[data-model-text]').forEach((el) => {
			if (el.textContent !== name) el.textContent = name;
		});

		const lower = name.toLowerCase();
		document.querySelectorAll('[data-bs-model-alts]').forEach((group) => {
			const alts = [...group.querySelectorAll('[data-bs-model-alt]')];
			if (!alts.length) return;

			let best = null;
			let bestLength = -1;
			alts.forEach((alt) => {
				const match = (alt.dataset.modelMatch || '').trim().toLowerCase();
				if (match && lower.includes(match) && match.length > bestLength) {
					best = alt;
					bestLength = match.length;
				}
			});
			if (!best) best = alts.find((alt) => !(alt.dataset.modelMatch || '').trim()) || alts[0];

			alts.forEach((alt) => {
				const show = alt === best;
				if (alt.hidden === show) alt.hidden = !show;
				alt.querySelectorAll('video').forEach((video) => {
					if (!show) {
						video.pause();
					} else if (video.dataset.bsPromoVideoReady && !reduced()) {
						video.play().catch(() => {});
					}
				});
			});
		});
	};

	/* Case-type copy variants (exact match on option2, per the data contract —
	   never contains: "Impact" is a substring of both its siblings). The empty
	   match is the alternate copy; a comma-separated list matches any entry. */
	const applyCaseContent = (caseType) => {
		const name = typeof caseType === 'string' ? caseType.trim().toLowerCase() : '';
		if (!name) return;

		document.querySelectorAll('[data-bs-case-alts]').forEach((group) => {
			const alts = [...group.querySelectorAll('[data-bs-case-alt]')];
			if (!alts.length) return;

			let best = alts.find((alt) => (alt.dataset.caseMatch || '')
				.split(',')
				.map((value) => value.trim().toLowerCase())
				.includes(name));
			if (!best) best = alts.find((alt) => !(alt.dataset.caseMatch || '').trim()) || alts[0];

			alts.forEach((alt) => {
				const show = alt === best;
				if (alt.hidden === show) alt.hidden = !show;
			});
		});
	};

	document.addEventListener('variant:change', (event) => {
		if (!(event.target instanceof Element) || !event.target.closest('.shopify-section--main-product')) return;
		const variant = event.detail?.variant;
		if (variant?.option1) applyModelContent(variant.option1);
		if (variant?.option2) applyCaseContent(variant.option2);
	});
})();


/* --- BLANC SPACE CUSTOM: journey tracking (PostHog) ---------------------------
   One guarded helper (window.bsTrack) plus the interaction events the 2026-09-07
   audit found missing: drawers/menu opening and closing, every added-to-cart
   sheet CTA, the sticky add-to-cart bar, filters, search, wishlist, phone model
   changes, shipping protection and Load more (captured where they happen; see
   design-search.js and the collection loader). Every event carries the
   remembered phone model as a PostHog super property. Nothing here may throw
   into shopping code. add_to_cart / checkout_started live in the funnel block
   above; purchase arrives from the Customer-events web pixel
   (docs/posthog-purchase-pixel.js). */
(() => {
	const capture = (name, properties) => {
		try {
			window.posthog?.capture?.(name, properties || {});
		} catch (error) {
			/* analytics must never interfere with shopping */
		}
	};
	window.bsTrack = capture;

	/* The Back-cache restore profiler (12 Oct - 1 Nov 2026) was removed once the card-containment
	   fix brought iPhone back-return INP on collections to 232 ms p75 (from 1,288). The window.bsMark?.()
	   calls left in theme.js, custom.js and smart-offer-engine.js are no-ops without it. */

	const registerModel = () => {
		try {
			const model = window.BlancSpaceDevicePreference?.get?.() || '';
			const source = window.BlancSpaceDevicePreference?.getSource?.() || null;
			let colour = null;
			try { colour = localStorage.getItem('blancspace.phoneColour') || null; } catch (e) { /* private mode */ }
			// phone_colour_pref = the finish the shopper last picked anywhere (null = never picked);
			// the pickers re-register it the moment it changes
			window.posthog?.register?.({ phone_model: model || null, phone_model_source: source, phone_colour_pref: colour });
		} catch (error) { /* super properties are best effort */ }
	};
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', registerModel);
	else registerModel();
	document.addEventListener('blancspace:phone-model-change', (event) => {
		const detail = event.detail || {};
		capture('phone_model_changed', { model: detail.model, source: detail.source, origin: detail.origin, locked: detail.locked });
		registerModel();
	});

	/* Drawers (vendor dialog events fire on the drawer element). */
	const drawerName = (element) => {
		if (!element || !(element instanceof Element)) return null;
		if (element.id === 'cart-drawer') return 'cart';
		switch (element.tagName) {
			case 'SEARCH-DRAWER': return 'search';
			case 'CART-NOTIFICATION-DRAWER': return 'added_sheet';
			case 'WISHLIST-DRAWER': return 'wishlist';
			case 'QUICK-BUY-DRAWER': return 'quick_buy';
			default: return null;
		}
	};
	const openedAt = new WeakMap();
	const cartCount = () => {
		const bubble = document.querySelector('cart-count');
		const value = bubble ? parseInt(bubble.textContent, 10) : NaN;
		return Number.isFinite(value) ? value : null;
	};
	document.addEventListener('dialog:after-show', (event) => {
		const name = drawerName(event.target);
		if (!name) return;
		openedAt.set(event.target, performance.now());
		capture('drawer_opened', { drawer: name, cart_item_count: cartCount() });
	}, true);
	document.addEventListener('dialog:after-hide', (event) => {
		const name = drawerName(event.target);
		if (!name) return;
		const started = openedAt.get(event.target);
		capture('drawer_closed', { drawer: name, open_ms: started ? Math.round(performance.now() - started) : null });
	}, true);

	/* The header menu is the pill itself expanding (no dialog event). */
	const storeHeader = document.querySelector('store-header');
	if (storeHeader && 'MutationObserver' in window) {
		let menuOpenedAt = 0;
		new MutationObserver(() => {
			const expanded = storeHeader.hasAttribute('expanded');
			const siblingOpen = document.querySelector('search-drawer[open], wishlist-drawer[open]') || document.getElementById('cart-drawer')?.open;
			if (expanded && !siblingOpen && !menuOpenedAt) {
				menuOpenedAt = performance.now();
				capture('menu_opened');
			} else if (!expanded && menuOpenedAt) {
				capture('menu_closed', { open_ms: Math.round(performance.now() - menuOpenedAt) });
				menuOpenedAt = 0;
			}
		}).observe(storeHeader, { attributes: true, attributeFilter: ['expanded'] });
	}

	/* Declarative clicks: any [data-bs-track="event"] element (data-bs-cta names the
	   control), the sticky add-to-cart bar, the collection toolbar pills, header
	   icons and menu links. Capture phase so a navigation cannot outrun it. */
	document.addEventListener('click', (event) => {
		const target = event.target instanceof Element ? event.target : null;
		if (!target) return;
		const tagged = target.closest('[data-bs-track]');
		if (tagged) {
			const props = { cta: tagged.dataset.bsCta || null, href: tagged.getAttribute('href') || null };
			if (tagged.dataset.bsTrack === 'added_sheet_cta') {
				const meter = document.querySelector('cart-notification-drawer [data-bogof-state], cart-notification-drawer .bogof-meter');
				props.sheet_state = tagged.closest('[data-bs-added-actions]')?.dataset.bsAddedActions || 'pair';
				props.cart_item_count = cartCount();
				if (meter) props.bogof_state = meter.dataset.bogofState || null;
			}
			capture(tagged.dataset.bsTrack, props);
		}
		if (target.closest('.product-quick-add button, .product-quick-add [type="submit"]')) {
			capture('sticky_atc_click', { label: target.closest('button')?.textContent?.trim().slice(0, 40) || null });
		}
		if (target.closest('[data-bs-model-pill]')) capture('collection_model_pill_opened');
		else if (target.closest('.bs-collection-toolbar__button')) capture('collection_filter_opened');
		const headerIcon = target.closest('store-header .header__icon-list a, store-header .header__icon-list button');
		if (headerIcon) {
			const label = (headerIcon.getAttribute('aria-label') || headerIcon.textContent || '').trim().toLowerCase().slice(0, 30);
			capture('header_icon_click', { icon: label || null });
		}
		const menuLink = target.closest('store-header[expanded] a[href*="/collections/"], store-header[expanded] a[href*="/pages/"], store-header[expanded] a[href*="/products/"]');
		if (menuLink) capture('menu_link_click', { href: menuLink.getAttribute('href'), text: menuLink.textContent.trim().slice(0, 40) });
	}, true);

	/* Filters and sort. Two layers so both "what do people touch" and "what do
	   they actually apply" can be ranked in PostHog:
	   - collection_filter_option_toggled: every change on a filter/sort control
	     (drawer or the vendor facets on /search), with group + value + label.
	   - collection_filters_applied (summary) + one collection_filter_value_applied
	     per applied value when the custom controller dispatches facet:update. */
	const filterGroupFor = (input) => {
		if (!input) return 'other';
		if (input.dataset.bsAestheticOption || input.name === 'filter.p.tag') return 'aesthetic';
		if (input.dataset.bsCaseType || /case.?type/i.test(input.name || '')) return 'case_type';
		if (input.dataset.bsBumperColour || /colou?r/i.test(input.name || '')) return 'colour';
		if (input.name === 'sort_by') return 'sort';
		if (/phone.?model/i.test(input.name || '')) return 'model';
		if (input.name?.startsWith('filter.v.price') || input.name?.startsWith('filter.p.price')) return 'price';
		if (input.name?.startsWith('filter.')) return input.name.replace(/^filter\./, '');
		return 'other';
	};
	document.addEventListener('change', (event) => {
		const input = event.target instanceof Element ? event.target.closest('input[name^="filter."], input[name="sort_by"], select[name="sort_by"]') : null;
		if (!input) return;
		const group = filterGroupFor(input);
		const label = input.dataset.bsFilterLabel || input.labels?.[0]?.textContent?.trim() || null;
		capture('collection_filter_option_toggled', {
			group,
			value: String(input.value || '').slice(0, 80),
			label: label ? label.slice(0, 60) : null,
			checked: input.type === 'checkbox' || input.type === 'radio' ? input.checked : true,
			surface: input.closest('#facet-form') ? 'drawer' : 'facets',
			collection: (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || (location.pathname.startsWith('/search') ? 'search' : null),
		});
	}, true);
	document.addEventListener('facet:update', (event) => {
		try {
			const url = new URL(event.detail?.url || location.href, location.href);
			const keys = [...url.searchParams.keys()];
			const collection = (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || (location.pathname.startsWith('/search') ? 'search' : null);
			const applied = [];
			url.searchParams.getAll('filter.p.tag').forEach((tag) => applied.push({ group: 'aesthetic', value: tag }));
			keys.forEach((key) => {
				if (!key.startsWith('filter.') || key === 'filter.p.tag') return;
				url.searchParams.getAll(key).forEach((value) => {
					if (!value) return;
					const group = /case.?type/i.test(key) ? 'case_type' : /colou?r/i.test(key) ? 'colour' : /phone.?model/i.test(key) ? 'model' : key.replace(/^filter\./, '');
					if (group === 'model') return; // device context, not a shopper choice
					applied.push({ group, value });
				});
			});
			const sort = url.searchParams.get('sort_by');
			if (sort) applied.push({ group: 'sort', value: sort });
			capture('collection_filters_applied', {
				filters: keys.filter((key) => key.startsWith('filter.')).length,
				aesthetics: url.searchParams.getAll('filter.p.tag').length,
				case_type: applied.find((entry) => entry.group === 'case_type')?.value || null,
				colour: applied.find((entry) => entry.group === 'colour')?.value || null,
				sort: sort || null,
				collection,
			});
			applied.forEach((entry) => capture('collection_filter_value_applied', { ...entry, collection }));
		} catch (error) { /* ignore malformed urls */ }
	});

	/* Search page form (the drawer's own search events are captured in design-search.js). */
	document.addEventListener('submit', (event) => {
		const form = event.target instanceof Element ? event.target.closest('form.bs-search-page-form, form[action$="/search"]') : null;
		if (!form) return;
		const query = (form.querySelector('input[name="q"]')?.value || '').trim();
		if (query) capture('search_submitted', { query: query.slice(0, 80), surface: 'search_page' });
	}, true);

	/* Wishlist, cart page, line changes. */
	document.addEventListener('blancspace:wishlist-change', (event) => {
		capture('wishlist_changed', { count: event.detail?.count ?? null });
	});
	if (window.themeVariables?.settings?.pageType === 'cart') capture('cart_page_viewed', { cart_item_count: cartCount() });
	document.addEventListener('cart:change', (event) => {
		const detail = event.detail || {};
		if (detail.baseEvent !== 'line-item:change') return;
		capture('cart_line_changed', { item_count: detail.cart?.item_count ?? null, total: typeof detail.cart?.total_price === 'number' ? detail.cart.total_price / 100 : null });
	});
})();

/* --- BLANC SPACE CUSTOM: deferred gallery media ---------------------------------
   snippets/product-gallery.liquid renders media outside the selected variant's
   group as empty stub cells ([data-bs-deferred]) instead of ~240 full <img>s.
   The vendor variant picker re-renders the section for every combination and
   dispatches `product:rerender` on the form with the fresh fragment; the gallery
   only copies `filtered-indexes` from it. This listener (capture phase — the
   event does not bubble) fills every stub whose real cell exists in the
   fragment before the gallery unhides it, so a model switch shows its images
   exactly as before. Cells are matched by data-media-id; the fragment for the
   new variant carries that variant's group fully rendered. */
(() => {
	const hydrate = (event) => {
		const form = event.target;
		const fragment = event.detail?.htmlFragment;
		// getAttribute, not form.id: the product form contains <input name="id">,
		// which shadows the id property with the element itself.
		const formId = form instanceof Element ? form.getAttribute('id') : null;
		if (!formId || !fragment?.querySelector) return;
		document.querySelectorAll(`product-gallery[form="${formId}"]`).forEach((gallery) => {
			const stubs = gallery.querySelectorAll('[data-bs-deferred]');
			if (!stubs.length) return;
			const fresh = fragment.querySelector(`product-gallery[form="${formId}"]`);
			if (!fresh) return;
			stubs.forEach((stub) => {
				const id = stub.getAttribute('data-media-id');
				if (!id) return;
				const selector = stub.matches('.product-gallery__thumbnail')
					? `.product-gallery__thumbnail[data-media-id="${id}"]:not([data-bs-deferred])`
					: `.product-gallery__media[data-media-id="${id}"]:not([data-bs-deferred])`;
				const real = fresh.querySelector(selector);
				if (!real) return;
				// Fill the stub in place rather than replacing the node: the vendor
				// carousel caches its `items` array, so the element identity must
				// survive for its hidden-by-index toggling to keep reaching this cell.
				stub.replaceChildren(...Array.from(real.cloneNode(true).childNodes));
				Array.from(real.attributes).forEach((attribute) => {
					if (attribute.name !== 'hidden') stub.setAttribute(attribute.name, attribute.value);
				});
				stub.removeAttribute('data-bs-deferred');
			});
		});
	};
	document.addEventListener('product:rerender', hydrate, true);
})();

/* --- BLANC SPACE: design-browsing surfaces → one comparable event -----------
   `pdp_browse_click` fires whenever a shopper moves from one design to another
   via an on-page browsing surface, with `surface` naming which one. ONE event
   with a breakdown, not one event per surface, because the question it exists
   to answer is comparative: does the More Designs rail earn its place in the
   buy box, measured against how shoppers browse otherwise (collection cards,
   the bottom "More like this" grid, search)? Position, origin and destination
   ride along so depth-of-browse and paths can be reconstructed. Guarded — the
   click must always still navigate. */
(() => {
	const surfaceFor = (link) => {
		const rail = link.closest('more-designs');
		if (rail) return { surface: rail.hasAttribute('data-collapsed') ? 'more_designs_rail' : 'more_designs_grid', container: rail.querySelector('[data-more-designs-list]') };
		const related = link.closest('.bs-related-grid');
		if (related) return { surface: 'related_grid', container: related };
		const card = link.closest('.bs-collection-card');
		const list = card && card.closest('product-list, .product-list');
		if (card && list) {
			const path = location.pathname;
			const surface = path.startsWith('/search') ? 'search_card' : path === '/' ? 'home_card' : 'collection_card';
			return { surface, container: list, card };
		}
		return null;
	};

	document.addEventListener(
		'click',
		(event) => {
			try {
				const link = event.target.closest?.('a[href*="/products/"]');
				if (!link) return;
				const hit = surfaceFor(link);
				if (!hit) return;
				const tiles = hit.container
					? Array.from(hit.container.querySelectorAll(hit.card ? '.bs-collection-card' : 'a.bs-related-tile')).filter((el) => !el.hidden)
					: [];
				const unit = hit.card || link.closest('a.bs-related-tile') || link;
				const position = tiles.indexOf(unit) + 1 || null;
				const toHandle = (link.getAttribute('href') || '').split('/products/')[1]?.split(/[?#]/)[0] || null;
				window.bsTrack?.('pdp_browse_click', {
					surface: hit.surface,
					position,
					tile_count: tiles.length || null,
					to_handle: toHandle,
					from_path: location.pathname,
					from_handle: location.pathname.startsWith('/products/') ? location.pathname.split('/products/')[1]?.split(/[?#]/)[0] : null,
				});
			} catch (error) {
				/* analytics must never block navigation */
			}
		},
		{ capture: true }
	);
})();


/* --- BLANC SPACE: variant option changes ----------------------------------
   `variant_option_changed` fires once per option that actually changed on a
   variant:change, so the question "how often do shoppers leave the default
   case type / colour, and which way do they go?" has an answer. `change_index`
   counts changes within the pageview (1 = the first departure from the
   default). Guarded; never touches the picker's own flow. */
(() => {
	let previous = null;
	let changeIndex = 0;
	const optionNames = ['Phone Model', 'Case Type', 'Colour'];
	// Seed from the page's initial variant: variant:change only fires on a CHANGE,
	// so without this the first departure from the default — the one that
	// matters most — was never recorded.
	const seed = () => {
		try {
			const initial = document.querySelector('.variant-picker-cases script[data-variant], variant-picker script[data-variant]');
			const variant = initial ? JSON.parse(initial.textContent) : null;
			if (variant && Array.isArray(variant.options)) previous = { id: variant.id, options: variant.options.slice() };
		} catch (error) {
			/* no seed — the first change will seed instead */
		}
	};
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', seed);
	else seed();
	document.addEventListener('variant:change', (event) => {
		try {
			const variant = event.detail?.variant;
			if (!variant || !Array.isArray(variant.options)) return;
			if (previous && previous.id !== variant.id) {
				variant.options.forEach((value, index) => {
					if (previous.options[index] === value) return;
					changeIndex += 1;
					window.bsTrack?.('variant_option_changed', {
						option: optionNames[index] || `option${index + 1}`,
						from: previous.options[index] ?? null,
						to: value,
						change_index: changeIndex,
						product_handle: location.pathname.split('/products/')[1]?.split(/[?#]/)[0] || null,
					});
				});
			}
			previous = { id: variant.id, options: variant.options.slice() };
		} catch (error) {
			/* analytics must never interfere with shopping */
		}
	});
})();

/* Gallery overlays step aside on marketing slides (2026-09-29).
   The feature badges (offer / drop / MagSafe), the phone-colour pill and the
   advisory note float over the product gallery; on the marketing slides that
   follow the three product shots they landed on the slides' own headlines.
   `carousel:select` bubbles from the vendor media-carousel with the slide
   element; the position counted over VISIBLE items (cells hidden by the
   variant filter don't count) decides whether the gallery is on a product
   shot (0-2) or a marketing slide (3+). CSS in custom.css fades the overlays. */
(() => {
	const gallery = document.querySelector('.shopify-section--main-product product-gallery');
	// only products on the colour-gallery engine (metafield blancspace.colour_gallery
	// renders the pill); every other product keeps today's gallery untouched, so the
	// theme release is a no-op until a product is flagged (Scott, 2026-10-06)
	if (!gallery || !gallery.querySelector('[data-bs-phone-colours], [data-bs-review]')) return; // also the customer-media slides (2026-10-08)
	const onSelect = (event) => {
		const slide = event.detail && event.detail.slide;
		const carousel = slide && slide.closest('media-carousel');
		if (!carousel) return;
		const visible = Array.isArray(carousel.visibleItems)
			? carousel.visibleItems
			: Array.from(carousel.querySelectorAll('.product-gallery__media')).filter((item) => !item.hidden);
		const position = visible.indexOf(slide);
		gallery.toggleAttribute('data-bs-marketing-slide', position > 2);
	};
	gallery.addEventListener('carousel:select', onSelect);
	gallery.addEventListener('carousel:change', onSelect);
})();
